import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { EmailBlockData } from '../ModernEmailBuilder';
import { BlockRenderer } from './BlockRenderer';
import { FOOTER_PALETTE_PREVIEW_HEIGHTS } from '../utils/footerBlockUtils';
import { FOOTER_PREBUILT_LAYOUTS } from '../utils/footerPresets';
import { FOOTER_BUILDER_CSS } from '../utils/footerResponsiveCss';
import { HERO_PALETTE_PREVIEW_HEIGHTS, HERO_PRESET_IMAGES } from '../utils/heroContentUtils';
import { createBlockFromType, getRandomLogo, getRandomHeaderColor } from '../utils/blockFactory';
import {
  DEFAULT_COMPACT_FEATURES,
  DEFAULT_COMPACT_SECTION_TITLE,
  DEFAULT_DARK_BAND_FEATURES,
  DEFAULT_DARK_BAND_SECTION_TITLE,
  DEFAULT_ICON_2X2_FEATURES,
  DEFAULT_ICON_2X2_SECTION_TITLE,
  DEFAULT_ICON_LIST_FEATURES,
  DEFAULT_ICON_LIST_SECTION_TITLE,
  DEFAULT_NUMBERED_FEATURES,
  DEFAULT_NUMBERED_SECTION_TITLE,
} from '../utils/featuresGridUtils';
import { savedBlocksService, SavedBlock } from '../../../services/savedBlocksService';
import { recentBlocksService } from '../../../services/recentBlocksService';
import { storageService } from '../../../services/storageService';
import { StorageFile } from '../../../types/storage';
import { LayoutTreePanel } from '../panels/LayoutTreePanel';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAuth } from '../../../contexts/AuthContext';
import { CanvaImportGuideContext } from '../../../contexts/CanvaImportGuideContext';
import ContentManager from '../../ContentManager';
import {
  PhotoIcon,
  DocumentIcon,
  CursorArrowRaysIcon,
  RectangleStackIcon,
  MinusIcon,
  ArrowsUpDownIcon,
  VideoCameraIcon,
  ShareIcon,
  Bars3Icon,
  CodeBracketIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  StarIcon,
  QrCodeIcon,
  ClockIcon,
  SparklesIcon,
  ChartBarIcon,
  ListBulletIcon,
  GifIcon,
  PlayIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';

// Simple H icon component
const HIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`font-bold ${className || ''}`}>H</span>
);

// Simple P icon component
const PIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`font-bold ${className || ''}`}>P</span>
);

interface BlockItem {
  type: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const getBlockTypeForFile = (file: StorageFile) => {
  if (file.type === 'media') return 'video';
  if (file.type === 'image' || file.type === 'graphic' || file.type === 'logo') return 'image';
  return 'button';
};

const DraggableContentFile: React.FC<{ file: StorageFile; draggableId: string; children: React.ReactNode }> = ({
  file,
  draggableId,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: draggableId,
    data: {
      fromPalette: true,
      type: getBlockTypeForFile(file),
      contentFile: file,
    },
  });

  const style = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`border border-slate-200 rounded-xl p-3 flex gap-3 items-center bg-white hover:border-slate-300 cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'opacity-50 scale-95' : ''
        }`}
    >
      {children}
    </div>
  );
};

const BrandingColorSwatch: React.FC<{ label: string; color?: string }> = ({ label, color }) => (
  <div className="flex items-center gap-3">
    <span
      className="w-10 h-10 rounded-lg border border-slate-200 shadow-inner"
      style={{ backgroundColor: color || '#E2E8F0' }}
    />
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{color || 'Not set'}</p>
    </div>
  </div>
);

const BrandingFontPreview: React.FC<{
  label: string;
  font?: string;
  weight?: 'normal' | 'bold';
  style?: 'normal' | 'italic';
  sample?: string;
}> = ({ label, font, weight = 'normal', style = 'normal', sample = 'The quick brown fox' }) => (
  <div className="p-3 rounded-lg border border-slate-100 bg-slate-50">
    <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</p>
    <p
      className="text-sm font-semibold text-slate-900"
      style={{ fontFamily: font || 'Inter', fontWeight: weight === 'bold' ? 600 : 400, fontStyle: style }}
    >
      {font || 'Not set'}
    </p>
    <p
      className="text-sm text-slate-600 mt-1 truncate"
      style={{ fontFamily: font || 'Inter', fontWeight: weight === 'bold' ? 600 : 400, fontStyle: style }}
    >
      {sample}
    </p>
  </div>
);

export const BLOCK_TYPES: BlockItem[] = [
  {
    type: 'image',
    label: 'Image',
    icon: PhotoIcon,
    description: 'Add an image',
  },
  {
    type: 'heading',
    label: 'Heading',
    icon: HIcon,
    description: 'Add a heading',
  },
  {
    type: 'text',
    label: 'Text',
    icon: PIcon,
    description: 'Add a text block',
  },
  {
    type: 'title',
    label: 'Title',
    icon: HIcon,
    description: 'Large display title',
  },
  {
    type: 'subtitle',
    label: 'Subtitle',
    icon: HIcon,
    description: 'Secondary heading',
  },
  {
    type: 'quote',
    label: 'Quote',
    icon: PIcon,
    description: 'Stylish blockquote',
  },
  {
    type: 'caption',
    label: 'Caption',
    icon: PIcon,
    description: 'Small descriptive text',
  },
  {
    type: 'preheader',
    label: 'Preheader',
    icon: PIcon,
    description: 'Preview text for inbox',
  },
  {
    type: 'list',
    label: 'List',
    icon: ListBulletIcon,
    description: 'Bulleted or numbered list',
  },
  {
    type: 'button',
    label: 'Button',
    icon: CursorArrowRaysIcon,
    description: 'Add a button',
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: MinusIcon,
    description: 'Add a divider line',
  },
  {
    type: 'spacer',
    label: 'Spacer',
    icon: ArrowsUpDownIcon,
    description: 'Add spacing',
  },

  {
    type: 'social',
    label: 'Social',
    icon: ShareIcon,
    description: 'Add social links',
  },
  {
    type: 'menu',
    label: 'Menu',
    icon: Bars3Icon,
    description: 'Add navigation links',
  },
  {
    type: 'header',
    label: 'Header',
    icon: RectangleStackIcon,
    description: 'Brand Header (Logo & Nav)',
  },
  {
    type: 'logo',
    label: 'Logo',
    icon: StarIcon,
    description: 'Add a logo',
  },
  {
    type: 'code',
    label: 'Code',
    icon: CodeBracketIcon,
    description: 'Add custom code',
  },
  {
    type: 'product',
    label: 'Product',
    icon: ShoppingBagIcon,
    description: 'Add a product',
  },
  {
    type: 'product-rec',
    label: 'Product Rec',
    icon: ShoppingCartIcon,
    description: 'Product recommendation',
  },
  {
    type: 'qr-code',
    label: 'QR Code',
    icon: QrCodeIcon,
    description: 'Add a QR code',
  },
  {
    type: 'star-rating',
    label: 'Star Rating',
    icon: StarIcon,
    description: 'Add a star rating',
  },
  {
    type: 'magic-text',
    label: 'Magic Text',
    icon: SparklesIcon,
    description: 'AI-generated text block',
  },
  {
    type: 'poll',
    label: 'Poll',
    icon: ChartBarIcon,
    description: 'Ask a question',
  },
  {
    type: 'accordion',
    label: 'Accordion',
    icon: ListBulletIcon,
    description: 'Collapsible content',
  },
  {
    type: 'image-carousel',
    label: 'Carousel',
    icon: PhotoIcon,
    description: 'Image gallery',
  },
  {
    type: 'giphy',
    label: 'GIF',
    icon: GifIcon,
    description: 'Add a GIF',
  },
  {
    type: 'sticker',
    label: 'Sticker',
    icon: FaceSmileIcon,
    description: 'Stickers & badges',
  },
  {
    type: 'video',
    label: 'Video',
    icon: PlayIcon,
    description: 'Add a video link with a preview thumbnail',
  },
  {
    type: 'countdown',
    label: 'Countdown',
    icon: ClockIcon,
    description: 'Add a countdown timer',
  },
];

interface LayoutTemplate {
  id: string;
  name: string;
  description: string;
  preview: string;
  blocks: any[];
  isBlank?: boolean;
  previewColumns?: number[];
}

// Blank Layout Templates
const BLANK_LAYOUTS: LayoutTemplate[] = [
  {
    id: 'blank-100',
    name: '100%',
    description: 'Single column full width',
    preview: '▬',
    isBlank: true,
    previewColumns: [100],
    blocks: [
      {
        section: 'body',
        type: 'columns',
        content: {
          columns: 1,
          columnWidths: ['100%'],
          columnContent: ['']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
  {
    id: 'blank-50-50',
    name: '50-50%',
    description: 'Two equal columns',
    preview: '▬▬',
    isBlank: true,
    previewColumns: [50, 50],
    blocks: [
      {
        type: 'columns',
        content: {
          columns: 2,
          columnWidths: ['50%', '50%'],
          columnContent: ['', '']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
  {
    id: 'blank-33-33-33',
    name: '33-33-33%',
    description: 'Three equal columns',
    preview: '▬▬▬',
    isBlank: true,
    previewColumns: [33.33, 33.33, 33.33],
    blocks: [
      {
        type: 'columns',
        content: {
          columns: 3,
          columnWidths: ['33.33%', '33.33%', '33.33%'],
          columnContent: ['', '', '']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
  {
    id: 'blank-30-70',
    name: '30-70%',
    description: 'Two columns: 30% and 70%',
    preview: '▬▬▬',
    isBlank: true,
    previewColumns: [30, 70],
    blocks: [
      {
        type: 'columns',
        content: {
          columns: 2,
          columnWidths: ['30%', '70%'],
          columnContent: ['', '']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
  {
    id: 'blank-70-30',
    name: '70-30%',
    description: 'Two columns: 70% and 30%',
    preview: '▬▬▬',
    isBlank: true,
    previewColumns: [70, 30],
    blocks: [
      {
        type: 'columns',
        content: {
          columns: 2,
          columnWidths: ['70%', '30%'],
          columnContent: ['', '']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
  {
    id: 'blank-25-25-25-25',
    name: '25-25-25-25%',
    description: 'Four equal columns',
    preview: '▬▬▬▬',
    isBlank: true,
    previewColumns: [25, 25, 25, 25],
    blocks: [
      {
        type: 'columns',
        content: {
          columns: 4,
          columnWidths: ['25%', '25%', '25%', '25%'],
          columnContent: ['', '', '', '']
        },
        styles: {
          padding: '20px',
        }
      },
    ],
  },
];

// Pre-built Layout Templates
const PREBUILT_LAYOUTS: LayoutTemplate[] = [
  ...FOOTER_PREBUILT_LAYOUTS,
  {
    id: 'layout-menu-classic',
    name: 'Menu - Classic',
    description: 'Clean horizontal text menu',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Home', url: '#' },
            { label: 'Products', url: '#' },
            { label: 'About', url: '#' },
            { label: 'Contact', url: '#' },
          ],
        },
        styles: {
          padding: '16px 24px',
          textAlign: 'center',
          color: '#334155',
          fontSize: '14px',
          fontWeight: '600',
          linkSpacing: '10px',
          buttonStyle: 'filled',
          backgroundColor: '#FFFFFF',
          containerBorderColor: '#E2E8F0',
          containerBorderRadius: '0',
          itemPadding: '8px 14px',
          itemBorderRadius: '0',
          itemBorderColor: '#CBD5E1',
          itemBackgroundColor: '#F8FAFC',
          itemTextColor: '#334155',
        },
      },
    ],
  },
  {
    id: 'layout-menu-dark-pill',
    name: 'Menu - Dark',
    description: 'Bold menu on dark background',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Shop', url: '#' },
            { label: 'New Arrivals', url: '#' },
            { label: 'Offers', url: '#' },
            { label: 'Support', url: '#' },
          ],
        },
        styles: {
          padding: '14px 24px',
          textAlign: 'center',
          color: '#F8FAFC',
          fontSize: '13px',
          fontWeight: '700',
          linkSpacing: '10px',
          buttonStyle: 'filled',
          backgroundColor: '#0F172A',
          containerBorderRadius: '0',
          itemPadding: '8px 14px',
          itemBorderRadius: '0',
          itemBorderColor: '#334155',
          itemBackgroundColor: '#1E293B',
          itemTextColor: '#F8FAFC',
        },
      },
    ],
  },
  {
    id: 'layout-menu-accent',
    name: 'Menu - Accent',
    description: 'Brand-style menu with accent color',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Features', url: '#' },
            { label: 'Pricing', url: '#' },
            { label: 'Integrations', url: '#' },
            { label: 'Docs', url: '#' },
          ],
        },
        styles: {
          padding: '15px 24px',
          textAlign: 'center',
          color: '#0F766E',
          fontSize: '14px',
          fontWeight: '700',
          linkSpacing: '10px',
          buttonStyle: 'filled',
          backgroundColor: '#ECFEFF',
          containerBorderColor: '#99F6E4',
          containerBorderRadius: '0',
          itemPadding: '8px 14px',
          itemBorderRadius: '0',
          itemBorderColor: '#5EEAD4',
          itemBackgroundColor: '#FFFFFF',
          itemTextColor: '#0F766E',
        },
      },
    ],
  },
  {
    id: 'layout-menu-glass',
    name: 'Menu - Glass Card',
    description: 'Soft card menu with elevated buttons',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Dashboard', url: '#' },
            { label: 'Reports', url: '#' },
            { label: 'Billing', url: '#' },
            { label: 'Settings', url: '#' },
          ],
        },
        styles: {
          padding: '16px 24px',
          textAlign: 'center',
          color: '#1E293B',
          fontSize: '13px',
          fontWeight: '700',
          linkSpacing: '10px',
          buttonStyle: 'filled',
          backgroundColor: '#F8FAFC',
          containerBorderColor: '#E2E8F0',
          containerBorderRadius: '0',
          containerShadow: '0 4px 16px rgba(15,23,42,0.06)',
          itemPadding: '9px 14px',
          itemBorderRadius: '0',
          itemBorderColor: '#E2E8F0',
          itemBackgroundColor: '#FFFFFF',
          itemShadow: '0 1px 2px rgba(15,23,42,0.08)',
          itemTextColor: '#1E293B',
        },
      },
    ],
  },
  {
    id: 'layout-menu-outline-clean',
    name: 'Menu - Outline Clean',
    description: 'Minimal outlined menu with square buttons',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Home', url: '#' },
            { label: 'Solutions', url: '#' },
            { label: 'Case Studies', url: '#' },
            { label: 'Contact', url: '#' },
          ],
        },
        styles: {
          padding: '18px 24px',
          textAlign: 'center',
          color: '#1F2937',
          fontSize: '14px',
          fontWeight: '600',
          linkSpacing: '12px',
          buttonStyle: 'outline',
          backgroundColor: '#FFFFFF',
          containerBorderColor: '#E5E7EB',
          containerBorderRadius: '0',
          itemPadding: '8px 16px',
          itemBorderRadius: '0',
          itemBorderColor: '#D1D5DB',
          itemBackgroundColor: '#FFFFFF',
          itemTextColor: '#1F2937',
        },
      },
    ],
  },
  {
    id: 'layout-menu-luxury',
    name: 'Menu - Luxury Gold',
    description: 'Elegant dark menu with gold-outline buttons',
    preview: 'Menu',
    blocks: [
      {
        type: 'menu',
        content: {
          links: [
            { label: 'Collections', url: '#' },
            { label: 'New Season', url: '#' },
            { label: 'Journal', url: '#' },
            { label: 'Book Visit', url: '#' },
          ],
        },
        styles: {
          padding: '16px 24px',
          textAlign: 'center',
          color: '#F8E7B5',
          fontSize: '13px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          linkSpacing: '10px',
          buttonStyle: 'outline',
          backgroundColor: '#111827',
          containerBorderColor: '#374151',
          containerBorderRadius: '0',
          itemPadding: '8px 14px',
          itemBorderRadius: '0',
          itemBorderColor: '#BFA15A',
          itemBackgroundColor: 'transparent',
          itemTextColor: '#F8E7B5',
        },
      },
    ],
  },
  // Content Layouts
  {
    id: 'layout-hero-content',
    name: 'Hero Content Block',
    description: 'Full-width hero with image, heading, text, and button',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
          heading: 'Introduce your concept',
          text: 'Use this space to introduce subscribers to the topic of this newsletter. If you\'re selling something, write informative and persuasive copy to effectively communicate your message.',
          buttonText: 'Button',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '12px 24px',
          headingColor: '#111827',
          textColor: '#6B7280',
          buttonColor: '#3B82F6',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '0',
          contentBackgroundColor: '#FFFFFF',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-hero-centered-dark',
    name: 'Hero – Centered (Dark)',
    description: 'Bold dark hero with centered heading, text, and button',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'dark-centered',
          heading: 'Welcome to your next campaign',
          text: 'Introduce your newsletter with a clear, punchy message that gets readers excited.',
          buttonText: 'Get Started',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '13px 28px',
          headingColor: '#FFFFFF',
          textColor: '#E5E7EB',
          buttonColor: '#22C55E',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '9999px',
        },
        styles: {
          padding: '0',
          backgroundColor: '#0F172A',
        },
      },
    ],
  },
  {
    id: 'layout-hero-split',
    name: 'Hero – Split Image',
    description: 'Two-column hero with text and button next to image',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'split',
          image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
          heading: 'Launch something new',
          text: 'Share a short description of your product, offer, or announcement.',
          buttonText: 'Learn more',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '11px 24px',
          headingColor: '#111827',
          textColor: '#4B5563',
          buttonColor: '#2563EB',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '0',
          contentBackgroundColor: '#FFFFFF',
          splitImageBackgroundColor: '#EFF6FF',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-hero-minimal',
    name: 'Hero – Minimal',
    description: 'Clean hero with large heading and subtle subtext',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'minimal',
          heading: 'A simple, clean introduction',
          text: 'Use a short paragraph to explain what this email is about and why it matters.',
          showButton: false,
          headingColor: '#111827',
          textColor: '#4B5563',
        },
        styles: {
          padding: '0',
          backgroundColor: '#F9FAFB',
          textAlign: 'left',
        },
      },
    ],
  },
  {
    id: 'layout-hero-centered-light',
    name: 'Hero – Centered (Light)',
    description: 'Minimal centered hero with eyebrow label and outline button',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'centered-light',
          subtitle: 'New collection',
          heading: 'Designed for clarity',
          text: 'A calm, focused introduction that puts your message first — no distractions.',
          buttonText: 'Explore',
          buttonUrl: '#',
          showButton: true,
          buttonStyle: 'outline',
          buttonPadding: '11px 22px',
          headingColor: '#111827',
          textColor: '#6B7280',
          eyebrowColor: '#9CA3AF',
          buttonColor: '#111827',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '0',
          contentBackgroundColor: '#FFFFFF',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
          textAlign: 'center',
        },
      },
    ],
  },
  {
    id: 'layout-hero-banner-image',
    name: 'Hero – Image Banner',
    description: 'Wide image strip with compact centered copy below',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'banner-image',
          image: HERO_PRESET_IMAGES.banner,
          heading: 'Work that inspires',
          text: 'Share a short update or announcement beneath a clean visual banner.',
          buttonText: 'Read more',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '10px 20px',
          headingColor: '#111827',
          textColor: '#6B7280',
          buttonColor: '#111827',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '0',
          contentBackgroundColor: '#FFFFFF',
          splitImageBackgroundColor: '#F3F4F6',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
          textAlign: 'center',
        },
      },
    ],
  },
  {
    id: 'layout-hero-accent-line',
    name: 'Hero – Accent Line',
    description: 'Editorial layout with left accent bar, no image',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'accent-line',
          subtitle: 'Featured',
          heading: 'Thoughtful design, delivered',
          text: 'Lead with a strong headline and supporting line — perfect for newsletters and product updates.',
          buttonText: 'View details',
          buttonUrl: '#',
          showButton: true,
          buttonStyle: 'outline',
          buttonPadding: '10px 20px',
          showAccentLine: true,
          accentLineWidth: 4,
          accentColor: '#111827',
          eyebrowColor: '#9CA3AF',
          headingColor: '#111827',
          textColor: '#6B7280',
          buttonColor: '#111827',
          buttonTextColor: '#FFFFFF',
          buttonBorderRadius: '0',
          contentBackgroundColor: '#FFFFFF',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
          textAlign: 'left',
        },
      },
    ],
  },
  {
    id: 'layout-hero-overlay-minimal',
    name: 'Hero – Overlay Minimal',
    description: 'Full-bleed image with soft gradient and bottom-aligned copy',
    preview: 'Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'overlay-minimal',
          image: HERO_PRESET_IMAGES.overlay,
          subtitle: 'Spring edit',
          heading: 'Stories worth sharing',
          text: 'Pair striking photography with concise copy for a polished, magazine-style opener.',
          buttonText: 'Discover',
          buttonUrl: '#',
          showButton: true,
          buttonStyle: 'outline',
          buttonPadding: '10px 20px',
          headingColor: '#FFFFFF',
          textColor: '#D1D5DB',
          eyebrowColor: 'rgba(255,255,255,0.75)',
          buttonColor: '#FFFFFF',
          buttonTextColor: '#111827',
          buttonBorderRadius: '0',
        },
        styles: {
          padding: '0',
          backgroundColor: '#111827',
          textAlign: 'left',
        },
      },
    ],
  },
  {
    id: 'layout-editorial-overlay',
    name: 'Hero – Overlay',
    description: 'High-impact hero with bold typography and product thumbnail overlayed on an image',
    preview: 'Editorial Overlay',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'image-overlay',
          heading: 'COMPLETE YOUR ORDER',
          text: 'Get 20% off your first purchase with code WELCOME20 at checkout.',
          buttonText: 'SHOP THE COLLECTION',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '12px 32px',
          image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80',
          buttonColor: '#FFFFFF',
          buttonTextColor: '#000000',
        },
        styles: {
          padding: '60px 40px',
          height: '600px',
          fontSize: '72px',
          lineHeight: '0.85',
          color: '#FFFFFF',
          justifyContent: 'flex-end',
          textAlign: 'left',
          backgroundColor: '#000000',
        },
      },
    ],
  },
  {
    id: 'layout-hero-card',
    name: 'Hero – Card',
    description: 'Modern editorial card with top image and overlapping content box',
    preview: 'Hero Card',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'hero-card',
          subtitle: 'SHOCKINGLY SOFT TEES',
          heading: "We're Bringing Basic Back",
          text: "Our jersey tees are soft, lightweight, and fit just right. And honestly...that's it. We're all about being basic and we don't care who knows.",
          buttonText: 'SHOP TEES',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '16px 48px',
          image: 'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?auto=format&fit=crop&w=1200&q=80',
          cardBackgroundColor: '#FFFFFF',
          textColor: '#000000',
          subtitleColor: '#6B7280',
          buttonColor: '#000000',
          buttonTextColor: '#FFFFFF',
        },
        styles: {
          padding: '0',
          backgroundColor: '#F3F4F6',
        },
      },
    ],
  },
  {
    id: 'layout-hero-editorial',
    name: 'Hero – Editorial',
    description: 'High-end editorial hero with serif typography and elegant spacing',
    preview: 'Editorial Hero',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'editorial',
          subtitle: 'SPRING / SUMMER 2024',
          heading: 'The Art of Minimal Living',
          text: 'Discover our curated collection of essentials designed for the modern home.',
          buttonText: 'Explore Collection',
          buttonUrl: '#',
          showButton: true,
          buttonPadding: '16px 40px',
          image: 'https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?auto=format&fit=crop&w=1200&q=80',
        },
        styles: {
          padding: '80px 40px',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-editorial-split',
    name: 'Editorial – Split',
    description: 'Elegant two-column editorial split with serif typography',
    preview: 'Editorial Split',
    blocks: [
      {
        type: 'image-content',
        content: {
          template: 'editorial',
          image: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?auto=format&fit=crop&w=1200&q=80',
          subtitle: 'THE COLLECTION',
          heading: 'Modern Craft & Timeless Design',
          text: 'Every piece in our sanctuary collection is handcrafted with sustainably sourced materials and a focus on essential form.',
          buttonText: 'Shop the Look',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-editorial-product',
    name: 'Editorial – Product',
    description: 'High-end single product showcase with serif typography',
    preview: 'Editorial Product',
    blocks: [
      {
        type: 'product',
        content: {
          template: 'editorial',
          image: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?auto=format&fit=crop&w=1200&q=80',
          subtitle: 'LIMITED EDITION',
          name: 'Signature High-Top Runner',
          description: 'A masterpiece of contemporary athletic design, featuring premium leather and our proprietary comfort sole technology.',
          price: '$220.00',
          buttonText: 'Buy Now',
          url: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-editorial-quote',
    name: 'Editorial – Quote',
    description: 'Large typography quote block with a minimalist aesthetic',
    preview: 'Editorial Quote',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'quote',
          text: 'Simplicity is the ultimate sophistication. It is not just how it looks, it is how it works.',
          subtitle: 'LEONARDO DA VINCI',
        },
        styles: {
          padding: '80px 20px',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-image-text-button',
    name: 'Image & Content',
    description: 'Two-column layout with image, text and button',
    preview: 'Image+Text',
    blocks: [
      {
        type: 'image-content',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1523475472560-d2df97ec485c?auto=format&fit=crop&w=1200&q=80',
          subtitle: 'Subtitle',
          heading: 'Introduce your concept',
          text: 'Use this space to introduce subscribers to the topic of this newsletter.',
          buttonText: 'Button',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-feature-showcase',
    name: 'Feature Showcase',
    description: 'Two-column feature showcase with image and content',
    preview: 'Feature',
    blocks: [
      {
        type: 'feature-showcase',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
          heading: 'Unmatched Creative Freedom',
          text: 'Design emails that stand out with our premium block system. Every component is crafted for high-end conversions and aesthetic excellence.',
          buttonText: 'Explore Features',
          buttonUrl: '#',
        },
        styles: {
          padding: '80px 40px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '40px 24px',
        },
      },
    ],
  },
  {
    id: 'layout-testimonial',
    name: 'Testimonial Card',
    description: 'Customer testimonial with quote and author',
    preview: 'Testimonial',
    blocks: [
      {
        type: 'testimonial',
        content: {
          template: 'single',
          layout: 'single',
          sectionTitle: 'Client Stories',
          testimonials: [
            {
              quote: 'The attention to detail and premium design aesthetic has completely elevated our brand communications.',
              author: 'SOPHIA CHEN',
              role: 'Product Director',
              image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80',
              rating: 5,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '80px 40px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '40px 24px',
        },
      },
    ],
  },
  {
    id: 'layout-cta-banner',
    name: 'Call-to-Action Banner',
    description: 'Bold CTA with heading, text, and prominent button',
    preview: 'CTA',
    blocks: [
      {
        type: 'cta-banner',
        content: {
          template: 'standard',
          heading: 'Level Up Your Strategy',
          text: 'Join 10,000+ creators who are building the future of email marketing with Scubamail.',
          buttonText: 'Try it for free',
          buttonUrl: '#',
        },
        styles: {
          padding: '100px 40px',
          backgroundColor: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
        },
        mobileStyles: {
          padding: '60px 24px',
        },
      },
    ],
  },
  {
    id: 'layout-profile-card',
    name: 'Profile Card',
    description: 'Profile and event details side-by-side',
    preview: 'Profile',
    blocks: [
      {
        type: 'profile-card',
        content: {
          template: 'standard',
          profileImage: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80',
          name: 'KENZI BARNES',
          role: 'Campaign manager',
          eventLabel: 'EVENT',
          eventTitle: 'Guest Lecture',
          eventDescription: 'Write event description here.',
          buttonText: 'Save my spot',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-product-grid-2',
    name: 'Products – 2 Columns',
    description: 'Two product cards with image, title, price, and button',
    preview: 'Product',
    blocks: [
      {
        type: 'product-rec',
        content: {
          products: [
            {
              name: 'Product name',
              price: '$99',
              image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80',
            },
            {
              name: 'Product name',
              price: '$99',
              image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&w=800&q=80',
            },
          ],
        },
        styles: {
          padding: '24px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '16px',
        },
      },
    ],
  },
  {
    id: 'layout-product-modern-grid',
    name: 'Products – Modern Grid',
    description: 'Elegant 3-column product grid with archival aesthetic',
    preview: 'Product',
    blocks: [
      {
        type: 'product-rec',
        content: {
          template: 'modern-grid',
          products: [
            {
              name: 'Signature Watch',
              price: '$85.00',
              image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80',
              buttonText: 'Shop Now',
            },
            {
              name: 'Classic Sunglasses',
              price: '$120.00',
              image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?auto=format&fit=crop&w=800&q=80',
              buttonText: 'Shop Now',
            },
            {
              name: 'Leather Essentials',
              price: '$45.00',
              image: 'https://images.unsplash.com/photo-1618453292459-53424b66bb6a?auto=format&fit=crop&w=800&q=80',
              buttonText: 'Shop Now',
            },
          ],
          showRoundedCorners: false,
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-product-hero',
    name: 'Product Hero',
    description: 'Large product image with centered title, price, and button',
    preview: 'Product',
    blocks: [
      {
        type: 'product',
        content: {
          name: 'Product name',
          description:
            'Describe your product in a way that highlights how it will benefit your readers.',
          price: '$99',
          image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=1200&q=80',
          url: '#',
        },
        styles: {
          padding: '24px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '16px',
        },
      },
    ],
  },
  // Music Launch Layouts (using the music-launch block type)
  {
    id: 'layout-music-launch-dark',
    name: 'Music Launch – Dark',
    description: 'Dark, cinematic hero for announcing a new single or album',
    preview: 'Music',
    blocks: [
      {
        type: 'music-launch',
        content: {
          layout: 'dark',
          coverImage:
            'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=80',
          title: 'New Single Out Now',
          artist: 'Your Artist Name',
          releaseType: 'Single',
          releaseDate: 'Available Today',
          description:
            'Turn up the volume on your latest release with bold visuals and a clear call-to-action.',
          primaryButtonText: 'Listen on Spotify',
          primaryButtonUrl: '#',
          secondaryButtonText: 'Watch the video',
          secondaryButtonUrl: '#',
          showSecondaryButton: true,
          showDescription: true,
          showTracklist: true,
          showPlatforms: true,
          tracks: [
            { title: 'Midnight City Lights', duration: '3:42', featured: '' },
            { title: 'Neon Dreams', duration: '3:18', featured: '' },
            { title: 'Afterglow', duration: '4:05', featured: 'Special Guest' },
          ],
          platforms: {
            spotifyUrl: '',
            appleMusicUrl: '',
            youtubeUrl: '',
            soundcloudUrl: '',
            bandcampUrl: '',
          },
        },
        styles: {
          padding: '0',
          backgroundColor: '#020617',
        },
      },
    ],
  },
  {
    id: 'layout-music-launch-light',
    name: 'Music Launch – Spotlight',
    description: 'Clean layout with album art, copy, and dual CTAs',
    preview: 'Music',
    blocks: [
      {
        type: 'music-launch',
        content: {
          layout: 'light',
          coverImage:
            'https://images.unsplash.com/photo-1453733190371-0a9bedd82893?auto=format&fit=crop&w=1200&q=80',
          title: 'Spotlight on Your New Release',
          artist: 'Artist Name',
          releaseType: 'EP',
          releaseDate: '',
          description:
            'Introduce your latest track or EP with a bright, editorial layout that looks great in every inbox.',
          primaryButtonText: 'Pre-save now',
          primaryButtonUrl: '#',
          secondaryButtonText: '',
          secondaryButtonUrl: '',
          showSecondaryButton: false,
          showDescription: true,
          showTracklist: false,
          showPlatforms: true,
          tracks: [],
          platforms: {
            spotifyUrl: '',
            appleMusicUrl: '',
            youtubeUrl: '',
            soundcloudUrl: '',
            bandcampUrl: '',
          },
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-music-launch-tour',
    name: 'Music + Tour Dates',
    description: 'Promote a new release with a tour schedule',
    preview: 'Music',
    blocks: [
      {
        type: 'music-launch',
        content: {
          layout: 'tour',
          coverImage:
            'https://images.unsplash.com/photo-1493225255756-d9584f8606e9?auto=format&fit=crop&w=1200&q=80',
          title: 'New Album & World Tour',
          artist: 'Your Band Name',
          releaseType: 'Album',
          releaseDate: 'Fall 2024',
          description:
            'Celebrate your new album with a global tour. Highlight key cities and dates to drive ticket sales.',
          primaryButtonText: 'Get Tickets',
          primaryButtonUrl: '#',
          secondaryButtonText: 'Listen to the album',
          secondaryButtonUrl: '#',
          showSecondaryButton: true,
          showDescription: true,
          showTracklist: false,
          showPlatforms: false,
          tracks: [
            { title: 'Sep 15, 2024', duration: '8:00 PM', featured: 'New York, NY' },
            { title: 'Sep 22, 2024', duration: '7:30 PM', featured: 'Chicago, IL' },
            { title: 'Sep 29, 2024', duration: '9:00 PM', featured: 'Los Angeles, CA' },
          ],
          platforms: {
            spotifyUrl: '',
            appleMusicUrl: '',
            youtubeUrl: '',
            soundcloudUrl: '',
            bandcampUrl: '',
          },
        },
        styles: {
          padding: '0',
          backgroundColor: '#020617',
        },
      },
    ],
  },
  // Product Launch Layout
  {
    id: 'layout-product-launch',
    name: 'Product Launch',
    description: 'Hero plus details for a new product launch',
    preview: 'Product',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'split',
          image:
            'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?auto=format&fit=crop&w=1200&q=80',
          heading: 'Introducing your next bestseller',
          text: 'Launch a new product with a clean hero, short pitch, and primary action button.',
          buttonText: 'Pre-order now',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
      {
        type: 'product',
        content: {
          name: 'Product name',
          description:
            'Use this space to highlight the top 2–3 benefits of your launch. Keep it short and focused on outcomes.',
          price: '$129',
          image:
            'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=1200&q=80',
          url: '#',
        },
        styles: {
          padding: '40px',
          backgroundColor: '#F9FAFB',
        },
        mobileStyles: {
          padding: '20px',
        },
      },
    ],
  },
  // Newsletter Layout
  {
    id: 'layout-newsletter-classic',
    name: 'Newsletter – Classic',
    description: 'Intro plus three neatly aligned content blocks for a newsletter',
    preview: 'Newsletter',
    blocks: [
      {
        type: 'newsletter',
        content: {
          layout: 'classic',
          image:
            'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=1200&q=80',
          showImage: true,
          title: 'This week at your brand',
          intro:
            'A short intro paragraph welcoming subscribers and setting expectations for this edition.',
          showButton: false,
          buttonText: '',
          buttonUrl: '',
          showSections: true,
          sections: [
            {
              title: 'Story 1',
              summary: 'Add a short headline and 2–3 lines of summary.',
              enabled: true,
            },
            {
              title: 'Story 2',
              summary: 'Highlight another important update or announcement.',
              enabled: true,
            },
            {
              title: 'Story 3',
              summary: 'Share a tip, resource, or link that adds value.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-product-grid-3',
    name: 'Products – 3 Columns',
    description: 'Three product cards in a row',
    preview: 'Product',
    blocks: [
      {
        type: 'product-rec',
        content: {
          products: [
            {
              name: 'Product 1',
              price: '$39.00',
              image: 'https://images.unsplash.com/photo-1528701800489-2050ebc5c9aa?auto=format&fit=crop&w=800&q=80',
            },
            {
              name: 'Product 2',
              price: '$39.00',
              image: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?auto=format&fit=crop&w=800&q=80',
            },
            {
              name: 'Product 3',
              price: '$39.00',
              image: 'https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=800&q=80',
            },
          ],
        },
        styles: {
          padding: '24px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '16px',
        },
      },
    ],
  },
  // Event Announcement Layouts
  {
    id: 'layout-event-announcement',
    name: 'Event Announcement',
    description: 'Hero with event details, date, and CTA',
    preview: 'Event',
    blocks: [
      {
        type: 'event-announcement',
        content: {
          layout: 'standard',
          heroImage: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1200&q=80',
          title: 'Future of Design Summit',
          description: 'A global gathering of visionaries, designers, and innovators reimagining the digital landscape.',
          buttonText: 'Secure Your Pass',
          buttonUrl: '#',
          showDetails: true,
          eventDate: 'Oct 24, 2024',
          eventTime: '10:00 AM PST',
          eventLocation: 'SF Design Studio',
        },
        styles: {
          padding: '0',
          backgroundColor: '#0F172A',
        },
      },
    ],
  },
  {
    id: 'layout-event-webinar',
    name: 'Webinar Invitation',
    description: 'Clean webinar signup with speaker info',
    preview: 'Event',
    blocks: [
      {
        type: 'event-announcement',
        content: {
          layout: 'webinar',
          heroImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80',
          title: 'Free Webinar: Master Your Craft',
          description: 'Join industry experts for an interactive session packed with actionable insights.',
          buttonText: 'Reserve Your Spot',
          buttonUrl: '#',
          showDetails: true,
          eventDate: 'Wednesday, November 20, 2024',
          eventTime: '2:00 PM - 3:30 PM EST',
          eventLocation: 'Online',
          additionalInfo: 'This webinar will cover essential strategies and best practices.',
          showSpeaker: true,
          speakerName: 'Dr. Sarah Johnson',
          speakerRole: 'Industry Expert & Author',
          speakerImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
          speakerBio: 'With over 15 years of experience, Dr. Johnson will share proven strategies and real-world examples.',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-event-elegant',
    name: 'Event – Elegant',
    description: 'Sophisticated event invitation with clean layout',
    preview: 'Event',
    blocks: [
      {
        type: 'event-announcement',
        content: {
          layout: 'elegant',
          heroImage: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=80',
          title: 'An Evening of Discovery',
          description: 'Join us for a private gallery opening and artist talk featuring the latest works from our summer collection.',
          eventDate: 'JUNE 24, 2024',
          eventTime: '7:00 PM',
          eventLocation: 'DASH GALLERY, NY',
          buttonText: 'RSVP Now',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#F9FAFB',
        },
      },
    ],
  },
  // Testimonial Layouts
  {
    id: 'layout-testimonial-single',
    name: 'Testimonial – Single',
    description: 'Featured customer testimonial with photo',
    preview: 'Testimonial',
    blocks: [
      {
        type: 'testimonial',
        content: {
          layout: 'single',
          sectionTitle: 'What Our Customers Say',
          testimonials: [
            {
              quote: 'This product changed my workflow completely. Highly recommend!',
              author: 'Jane Smith',
              role: 'CEO',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
              rating: 5,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-testimonial-grid',
    name: 'Testimonials – 3 Column',
    description: 'Three customer testimonials side by side',
    preview: 'Testimonial',
    blocks: [
      {
        type: 'testimonial',
        content: {
          layout: 'grid',
          sectionTitle: 'Trusted by Thousands',
          testimonials: [
            {
              quote: 'Amazing service and support. Exceeded all expectations!',
              author: 'Alex Johnson',
              role: '',
              image: '',
              rating: 5,
              enabled: true,
            },
            {
              quote: 'Best investment I\'ve made this year. Highly recommend!',
              author: 'Maria Garcia',
              role: '',
              image: '',
              rating: 5,
              enabled: true,
            },
            {
              quote: 'Incredible results in just a few weeks. Worth every penny!',
              author: 'David Chen',
              role: '',
              image: '',
              rating: 5,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  {
    id: 'layout-testimonial-creative',
    name: 'Testimonial – Creative',
    description: 'Modern testimonial with a unique star-shaped photo',
    preview: 'Testimonial',
    blocks: [
      {
        type: 'testimonial',
        content: {
          layout: 'creative-star',
          testimonials: [
            {
              quote: 'Working with this team has completely transformed our brand identity. Their attention to detail is unmatched.',
              author: 'Elena Rossi',
              role: 'Creative Director',
              image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFF1F2',
        },
      },
    ],
  },
  // Pricing Layouts
  {
    id: 'layout-pricing-table',
    name: 'Pricing Table',
    description: 'Three-tier pricing comparison',
    preview: 'Pricing',
    blocks: [
      {
        type: 'pricing-table',
        content: {
          sectionTitle: 'Membership Tiers',
          plans: [
            {
              name: 'BASIC',
              price: '$0',
              period: 'month',
              description: 'For hobbyists',
              features: ['3 Workspaces', 'Basic Analytics', 'Standard Support'],
              buttonText: 'Start Free',
              buttonUrl: '#',
              highlighted: false,
              enabled: true,
            },
            {
              name: 'PREMIUM',
              price: '$49',
              period: 'month',
              description: 'For professionals',
              features: ['Unlimited Workspaces', 'Advanced Insights', 'Priority Support', 'Custom Branding'],
              buttonText: 'Upgrade Now',
              buttonUrl: '#',
              highlighted: true,
              enabled: true,
            },
            {
              name: 'STUDIO',
              price: '$99',
              period: 'month',
              description: 'For larger teams',
              features: ['Everything in Premium', 'Team Management', 'White Label', '24/7 Support'],
              buttonText: 'Contact Sales',
              buttonUrl: '#',
              highlighted: false,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '60px 20px',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '40px 16px',
        },
      },
    ],
  },
  // Feature Highlight Layouts
  {
    id: 'layout-features-grid',
    name: 'Features – Grid',
    description: 'Feature highlights with icons in a grid',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'cards',
          sectionTitle: 'Why Choose Us',
          features: [
            {
              icon: '⚡',
              title: 'Fast & Reliable',
              description: 'Lightning-fast performance with 99.9% uptime guarantee.',
              enabled: true,
            },
            {
              icon: '🔒',
              title: 'Secure & Private',
              description: 'Enterprise-grade security to protect your data.',
              enabled: true,
            },
            {
              icon: '💡',
              title: 'Easy to Use',
              description: 'Intuitive interface that anyone can master quickly.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  {
    id: 'layout-features-icon-grid',
    name: 'Features – Icon Grid (2×2)',
    description: 'Left-aligned heading with icon feature grid',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'icon-2x2',
          sectionTitle: DEFAULT_ICON_2X2_SECTION_TITLE,
          features: DEFAULT_ICON_2X2_FEATURES.map((feature) => ({ ...feature })),
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  {
    id: 'layout-features-icon-list',
    name: 'Features – Icon List',
    description: 'Stacked rows with icon on the left',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'icon-list',
          sectionTitle: DEFAULT_ICON_LIST_SECTION_TITLE,
          features: DEFAULT_ICON_LIST_FEATURES.map((feature) => ({ ...feature })),
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  {
    id: 'layout-features-numbered',
    name: 'Features – Numbered Steps',
    description: 'Step-by-step numbered feature layout',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'numbered',
          sectionTitle: DEFAULT_NUMBERED_SECTION_TITLE,
          features: DEFAULT_NUMBERED_FEATURES.map((feature) => ({ ...feature })),
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  {
    id: 'layout-features-compact',
    name: 'Features – Compact (4 col)',
    description: 'Four compact columns with icons',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'compact-4',
          sectionTitle: DEFAULT_COMPACT_SECTION_TITLE,
          features: DEFAULT_COMPACT_FEATURES.map((feature) => ({ ...feature })),
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  {
    id: 'layout-features-dark',
    name: 'Features – Dark Highlight',
    description: 'Features on a dark background',
    preview: 'Features',
    blocks: [
      {
        type: 'features-grid',
        content: {
          layoutVariant: 'dark-band',
          sectionTitle: DEFAULT_DARK_BAND_SECTION_TITLE,
          features: DEFAULT_DARK_BAND_FEATURES.map((feature) => ({ ...feature })),
        },
        styles: {
          padding: '0',
          backgroundColor: '#111827',
        },
        mobileStyles: {
          padding: '0',
        },
      },
    ],
  },
  // Promotional Banner Layouts
  {
    id: 'layout-promo-banner',
    name: 'Promotional Banner',
    description: 'Eye-catching promotional offer with countdown',
    preview: 'Promo',
    blocks: [
      {
        type: 'promo-banner',
        content: {
          layout: 'with-countdown',
          heroImage: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=80',
          title: 'Limited Time Offer',
          description: 'Get 50% off your first purchase. Use code SAVE50 at checkout.',
          buttonText: 'Shop Now',
          buttonUrl: '#',
          showCountdown: true,
          countdownDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          countdownTitle: 'Offer Ends In',
        },
        styles: {
          padding: '0',
          backgroundColor: '#DC2626',
        },
      },
    ],
  },
  {
    id: 'layout-cta-simple',
    name: 'Simple CTA Banner',
    description: 'Clean call-to-action with minimal design',
    preview: 'CTA',
    blocks: [
      {
        type: 'cta-banner',
        content: {
          template: 'standard',
          heading: 'Ready to Get Started?',
          text: 'Join thousands of satisfied customers and transform your workflow today.',
          buttonText: 'Start Free Trial',
          buttonUrl: '#',
        },
        styles: {
          padding: '40px',
          backgroundColor: '#0F172A',
        },
        mobileStyles: {
          padding: '30px 20px',
        },
      },
    ],
  },
  // Blog Post Preview Layout
  {
    id: 'layout-blog-preview',
    name: 'Blog Post Preview',
    description: 'Featured blog post with image and excerpt',
    preview: 'Blog',
    blocks: [
      {
        type: 'blog-preview',
        content: {
          image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200&q=80',
          category: 'Latest Blog Post',
          title: '10 Tips to Improve Your Productivity',
          excerpt: 'Discover proven strategies to boost your productivity and achieve more in less time. Learn from industry experts and implement these tips today.',
          buttonText: 'Read More',
          buttonUrl: '#',
          showImage: true,
          showCategory: true,
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // FAQ Layout
  {
    id: 'layout-faq',
    name: 'FAQ Section',
    description: 'Frequently asked questions in an accordion style',
    preview: 'FAQ',
    blocks: [
      {
        type: 'faq-section',
        content: {
          sectionTitle: 'Frequently Asked Questions',
          faqs: [
            {
              question: 'How do I get started?',
              answer: 'Simply sign up for a free account and follow our quick setup guide. You\'ll be up and running in minutes.',
              enabled: true,
            },
            {
              question: 'What payment methods do you accept?',
              answer: 'We accept all major credit cards, PayPal, and bank transfers for enterprise plans.',
              enabled: true,
            },
            {
              question: 'Can I cancel anytime?',
              answer: 'Yes, you can cancel your subscription at any time. No questions asked, no hidden fees.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // Team Member Layout
  {
    id: 'layout-team-grid',
    name: 'Team Members – Grid',
    description: 'Showcase your team with photos and roles',
    preview: 'Team',
    blocks: [
      {
        type: 'team-grid',
        content: {
          sectionTitle: 'Meet Our Team',
          members: [
            {
              name: 'John Doe',
              role: 'CEO & Founder',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Jane Smith',
              role: 'Head of Design',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Mike Johnson',
              role: 'Lead Developer',
              image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // Newsletter Variations
  {
    id: 'layout-newsletter-minimal',
    name: 'Newsletter – Minimal',
    description: 'Clean single-column newsletter layout',
    preview: 'Newsletter',
    blocks: [
      {
        type: 'newsletter',
        content: {
          layout: 'single-column',
          image: '',
          showImage: false,
          title: 'Weekly Update',
          intro: 'Here\'s what happened this week and what\'s coming next.',
          showButton: false,
          buttonText: '',
          buttonUrl: '',
          showSections: true,
          sections: [
            {
              title: 'Big Announcement',
              summary: 'We\'re excited to share some major updates with you. Read more about our latest features and improvements.',
              enabled: true,
            },
            {
              title: 'Tips & Tricks',
              summary: 'Learn how to get the most out of our platform with these expert tips.',
              enabled: true,
            },
            {
              title: 'Community Spotlight',
              summary: 'See what our community has been up to and join the conversation.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // Flash Sale Template
  {
    id: 'layout-flash-sale',
    name: 'Flash Sale',
    description: 'Bold countdown-style sale announcement with urgency',
    preview: 'Sale',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'dark-centered',
          image: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?auto=format&fit=crop&w=1200&q=80',
          heading: '⚡ FLASH SALE ⚡',
          text: '48 HOURS ONLY — UP TO 70% OFF\nDon\'t miss out on our biggest sale of the season. Limited stock available.',
          buttonText: 'SHOP NOW',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#DC2626',
        },
      },
      {
        type: 'product-rec',
        content: {
          products: [
            {
              name: 'Best Seller',
              price: '$49.99',
              originalPrice: '$99.99',
              image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80',
            },
            {
              name: 'Top Pick',
              price: '$39.99',
              originalPrice: '$79.99',
              image: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?auto=format&fit=crop&w=800&q=80',
            },
          ],
        },
        styles: {
          padding: '24px',
          backgroundColor: '#0F172A',
        },
      },
    ],
  },
  // Cart Abandonment Template
  {
    id: 'layout-cart-abandonment',
    name: 'Cart Abandonment',
    description: 'Reminder email with abandoned product and CTA',
    preview: 'Cart',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'minimal',
          image: '',
          heading: 'Did you forget something?',
          text: 'You left some great items in your cart. Complete your order now before they sell out!',
          buttonText: '',
          buttonUrl: '',
        },
        styles: {
          padding: '40px',
          backgroundColor: '#FEF3C7',
        },
      },
      {
        type: 'product',
        content: {
          name: 'Your item is waiting',
          description: 'This popular item is selling fast. Don\'t miss your chance to get it before it\'s gone.',
          price: '$79.00',
          image: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?auto=format&fit=crop&w=1200&q=80',
          url: '#',
        },
        styles: {
          padding: '24px',
          backgroundColor: '#FFFFFF',
        },
      },
      {
        type: 'cta-banner',
        content: {
          template: 'standard',
          heading: 'Complete Your Order',
          text: 'Use code COMEBACK10 for 10% off your order.',
          buttonText: 'Return to Cart',
          buttonUrl: '#',
        },
        styles: {
          padding: '32px',
          backgroundColor: '#3B82F6',
        },
      },
    ],
  },
  // SaaS Welcome Email Template
  {
    id: 'layout-saas-welcome',
    name: 'SaaS Welcome',
    description: 'Onboarding welcome email with feature highlights',
    preview: 'Welcome',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'minimal',
          image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1200&q=80',
          heading: 'Welcome to [Your App]! 🎉',
          text: 'We\'re thrilled to have you on board. Here\'s everything you need to get started and make the most of your new account.',
          buttonText: 'Go to Dashboard',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
      {
        type: 'features-grid',
        content: {
          sectionTitle: 'Getting Started',
          features: [
            {
              icon: '1️⃣',
              title: 'Complete Your Profile',
              description: 'Add your details and customize your experience.',
              enabled: true,
            },
            {
              icon: '2️⃣',
              title: 'Explore the Dashboard',
              description: 'Discover all the tools at your fingertips.',
              enabled: true,
            },
            {
              icon: '3️⃣',
              title: 'Connect Your Apps',
              description: 'Integrate with your favorite tools.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#F8FAFC',
        },
      },
    ],
  },
  // Restaurant Menu Special Template
  {
    id: 'layout-restaurant-special',
    name: 'Restaurant Special',
    description: 'Featured dish showcase with appetizing imagery',
    preview: 'Food',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80',
          heading: 'Chef\'s Special This Week',
          text: 'Indulge in our signature seasonal creation, crafted with the freshest local ingredients.',
          buttonText: 'Reserve a Table',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#1F2937',
        },
      },
      {
        type: 'image-content',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=800&q=80',
          subtitle: 'THIS WEEK ONLY',
          heading: 'Truffle Risotto with Wild Mushrooms',
          text: 'A rich, creamy Arborio rice dish infused with black truffle oil and topped with a medley of wild mushrooms. Served with a side of garlic bread.\n\n$28.95',
          buttonText: 'View Full Menu',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // Real Estate Property Listing Template
  {
    id: 'layout-property-listing',
    name: 'Property Listing',
    description: 'Single property showcase with details and gallery',
    preview: 'Property',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'standard',
          image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80',
          heading: 'New Listing: Modern Family Home',
          text: '4 Bed • 3 Bath • 2,450 sq ft\n123 Maple Street, Pleasant Valley',
          buttonText: 'Schedule a Viewing',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
      {
        type: 'features-grid',
        content: {
          sectionTitle: 'Property Highlights',
          features: [
            {
              icon: '🏠',
              title: 'Open Floor Plan',
              description: 'Spacious living area with natural light.',
              enabled: true,
            },
            {
              icon: '🌳',
              title: 'Large Backyard',
              description: 'Perfect for entertaining and family time.',
              enabled: true,
            },
            {
              icon: '🚗',
              title: '2-Car Garage',
              description: 'Plus additional driveway parking.',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#F9FAFB',
        },
      },
      {
        type: 'cta-banner',
        content: {
          template: 'standard',
          heading: '$595,000',
          text: 'Contact us today to schedule a private showing.',
          buttonText: 'Contact Agent',
          buttonUrl: '#',
        },
        styles: {
          padding: '40px',
          backgroundColor: '#1E40AF',
        },
      },
    ],
  },
  // Fitness Class Schedule Template
  {
    id: 'layout-fitness-classes',
    name: 'Fitness Class Schedule',
    description: 'Weekly class schedule with times and instructors',
    preview: 'Fitness',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'dark-centered',
          image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1200&q=80',
          heading: 'This Week\'s Classes',
          text: 'Find the perfect workout for your schedule. All levels welcome!',
          buttonText: 'Book a Class',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#0F172A',
        },
      },
      {
        type: 'features-grid',
        content: {
          sectionTitle: 'Weekly Schedule',
          features: [
            {
              icon: '🧘',
              title: 'Monday 7AM',
              description: 'Sunrise Yoga with Sarah',
              enabled: true,
            },
            {
              icon: '💪',
              title: 'Tuesday 6PM',
              description: 'HIIT Training with Mike',
              enabled: true,
            },
            {
              icon: '🚴',
              title: 'Wednesday 5:30PM',
              description: 'Spin Class with Jessica',
              enabled: true,
            },
            {
              icon: '🏃',
              title: 'Thursday 7AM',
              description: 'Boot Camp with Alex',
              enabled: true,
            },
            {
              icon: '🧘‍♀️',
              title: 'Friday 6PM',
              description: 'Power Yoga with Sarah',
              enabled: true,
            },
            {
              icon: '🏋️',
              title: 'Saturday 9AM',
              description: 'Strength & Conditioning',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
    ],
  },
  // Conference Invite Template
  {
    id: 'layout-conference-invite',
    name: 'Conference Invitation',
    description: 'Multi-speaker conference with agenda preview',
    preview: 'Conference',
    blocks: [
      {
        type: 'hero-content',
        content: {
          template: 'dark-centered',
          image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1200&q=80',
          heading: 'Tech Summit 2024',
          text: 'Join 500+ industry leaders for the year\'s most anticipated technology conference.\n\nMarch 15-17, 2024 • San Francisco, CA',
          buttonText: 'Get Your Tickets',
          buttonUrl: '#',
        },
        styles: {
          padding: '0',
          backgroundColor: '#7C3AED',
        },
      },
      {
        type: 'team-grid',
        content: {
          sectionTitle: 'Featured Speakers',
          members: [
            {
              name: 'Dr. Sarah Chen',
              role: 'AI Research Lead, TechCorp',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Michael Torres',
              role: 'CTO, StartupXYZ',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
            {
              name: 'Emily Watson',
              role: 'VP Engineering, BigTech',
              image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80',
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#FFFFFF',
        },
      },
      {
        type: 'pricing-table',
        content: {
          sectionTitle: 'Ticket Options',
          plans: [
            {
              name: 'Early Bird',
              price: '$299',
              period: '',
              description: 'Limited availability',
              features: ['All sessions access', 'Conference materials', 'Networking reception'],
              buttonText: 'Get Early Bird',
              buttonUrl: '#',
              highlighted: false,
              enabled: true,
            },
            {
              name: 'VIP Pass',
              price: '$599',
              period: '',
              description: 'Best value',
              features: ['Everything in Early Bird', 'VIP lounge access', 'Speaker meet & greet', 'Recording access'],
              buttonText: 'Get VIP Pass',
              buttonUrl: '#',
              highlighted: true,
              enabled: true,
            },
          ],
        },
        styles: {
          padding: '0',
          backgroundColor: '#F8FAFC',
        },
      },
    ],
  },
  // Header Layouts
  {
    id: 'layout-header-modern-clean',
    name: 'Header - Modern Clean',
    description: 'Simple logo left, links right',
    preview: 'Header',
    blocks: [
      {
        type: 'header',
        content: {
          layout: 'modern-clean',
          logoUrl: '/images/Email Builder/Pastel&Co..png',
          logoMaxWidth: '120px',
          navTextColor: '#F8FAFC',
          links: [
            { label: 'Products', url: '#' },
            { label: 'About Us', url: '#' },
            { label: 'Contact', url: '#' },
          ],
        },
        styles: { padding: '16px 24px', backgroundColor: '#0F172A' },
      },
    ],
  },
  {
    id: 'layout-header-bold-centered',
    name: 'Header - Bold Centered',
    description: 'Logo centered above bold links',
    preview: 'Header',
    blocks: [
      {
        type: 'header',
        content: {
          layout: 'bold-centered',
          logoUrl: '/images/Email Builder/Pastel&Co..png',
          logoMaxWidth: '140px',
          navTextColor: '#1E293B',
          links: [
            { label: 'Shop', url: '#' },
            { label: 'New Arrivals', url: '#' },
            { label: 'Clearance', url: '#' },
          ],
        },
        styles: { padding: '28px 20px', backgroundColor: '#FDF2F8' },
      },
    ],
  },
  {
    id: 'layout-header-action-area',
    name: 'Header - Action Area',
    description: 'Logo left, links center, CTA button right',
    preview: 'Header',
    blocks: [
      {
        type: 'header',
        content: {
          layout: 'action',
          logoUrl: '/images/Email Builder/MajentaMantis.png',
          logoMaxWidth: '110px',
          navTextColor: '#334155',
          actionText: 'Book Now',
          actionUrl: '#',
          actionButtonBg: '#FFFFFF',
          actionButtonColor: '#1E293B',
          links: [
            { label: 'Services', url: '#' },
            { label: 'Pricing', url: '#' },
          ],
        },
        styles: { padding: '16px 20px', backgroundColor: '#FFFBEB' },
      },
    ],
  },
  {
    id: 'layout-header-dark-accent',
    name: 'Header - Dark Accent',
    description: 'Centered logo with accent highlight link',
    preview: 'Header',
    blocks: [
      {
        type: 'header',
        content: {
          layout: 'dark-accent',
          logoUrl: '/images/Email Builder/EdgeKart.png',
          logoMaxWidth: '130px',
          navTextColor: '#475569',
          accentLinkColor: '#CA8A04',
          links: [
            { label: 'Home', url: '#' },
            { label: 'Services', url: '#' },
            { label: 'Blog', url: '#' },
          ],
        },
        styles: { padding: '28px 20px', backgroundColor: '#F8FAFC' },
      },
    ],
  },
];

interface DraggableBlockProps {
  block: BlockItem;
}

const DraggableBlock: React.FC<DraggableBlockProps> = ({ block }) => {
  const blockIdRef = React.useRef(`block-${block.type}-${Math.random().toString(36).substring(2, 9)}`);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: blockIdRef.current,
    data: {
      fromPalette: true,
      type: block.type,
    },
  });

  const style = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2.5 px-3 py-2 border border-black/[0.07] rounded-md bg-white hover:bg-slate-50 hover:border-black/15 cursor-grab active:cursor-grabbing transition-all select-none ${isDragging ? 'opacity-50 scale-[0.98]' : ''
        }`}
    >
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-slate-400">
        <block.icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[12px] font-medium text-slate-700 leading-tight">{block.label}</div>
        <div className="text-[10px] text-slate-400 leading-tight mt-0.5">{block.description}</div>
      </div>
    </div>
  );
};

// Text preview component for Text & Headings - shows actual styled text preview
interface DraggableTextPreviewProps {
  type: string;
  previewText: string;
  style: React.CSSProperties;
}

const DraggableTextPreview: React.FC<DraggableTextPreviewProps> = ({ type, previewText, style }) => {
  const blockIdRef = React.useRef(`text-preview-${type}-${Math.random().toString(36).substring(2, 9)}`);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: blockIdRef.current,
    data: {
      fromPalette: true,
      type: type,
    },
  });

  const transformStyle = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={transformStyle}
      {...listeners}
      {...attributes}
      className={`py-3 px-4 border border-transparent rounded-lg bg-white hover:border-ocean-500 hover:bg-ocean-50 cursor-grab active:cursor-grabbing transition-all select-none ${isDragging ? 'opacity-50 scale-95' : ''
        }`}
    >
      <span style={style}>{previewText}</span>
    </div>
  );
};

// Button preview component for Buttons - shows styled button previews
interface ButtonStyleConfig {
  id: string;
  label: string;
  buttonText: string;
  buttonStyle: React.CSSProperties;
  hoverClass?: string;
}

const BUTTON_STYLES: ButtonStyleConfig[] = [
  // Gradient CTA
  {
    id: 'gradient-cta',
    label: 'Gradient CTA',
    buttonText: 'Get Started',
    buttonStyle: {
      background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '13px',
      border: 'none',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    },
  },
  // Solid Blue
  {
    id: 'solid-blue',
    label: 'Solid Blue',
    buttonText: 'Learn More',
    buttonStyle: {
      backgroundColor: '#3B82F6',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '6px',
      fontWeight: 500,
      fontSize: '13px',
      border: 'none',
    },
  },
  // Green Pill
  {
    id: 'green-pill',
    label: 'Green Pill',
    buttonText: 'Shop Now',
    buttonStyle: {
      backgroundColor: '#10B981',
      color: '#FFFFFF',
      padding: '6px 16px',
      borderRadius: '50px',
      fontWeight: 600,
      fontSize: '13px',
      border: 'none',
    },
  },
  // Outline
  {
    id: 'outline',
    label: 'Outline',
    buttonText: 'View More',
    buttonStyle: {
      backgroundColor: 'transparent',
      color: '#3B82F6',
      padding: '6px 14px',
      borderRadius: '6px',
      fontWeight: 500,
      fontSize: '13px',
      border: '1.5px solid #3B82F6',
    },
  },
  // 3D Orange
  {
    id: '3d-orange',
    label: '3D Orange',
    buttonText: 'Click Me',
    buttonStyle: {
      backgroundColor: '#F59E0B',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '6px',
      fontWeight: 700,
      fontSize: '12px',
      border: 'none',
      boxShadow: '0 3px 0 0 #D97706',
    },
  },
  // Dark Outline
  {
    id: 'dark-outline',
    label: 'Dark Outline',
    buttonText: 'Contact',
    buttonStyle: {
      backgroundColor: 'transparent',
      color: '#1E293B',
      padding: '6px 14px',
      borderRadius: '6px',
      fontWeight: 500,
      fontSize: '13px',
      border: '1.5px solid #334155',
    },
  },
  // Red Square
  {
    id: 'red-square',
    label: 'Red Square',
    buttonText: 'BUY',
    buttonStyle: {
      backgroundColor: '#DC2626',
      color: '#FFFFFF',
      padding: '8px 16px',
      borderRadius: '0',
      fontWeight: 700,
      fontSize: '12px',
      border: 'none',
      letterSpacing: '2px',
    },
  },
  // Purple Rounded
  {
    id: 'purple-rounded',
    label: 'Purple Rounded',
    buttonText: 'Subscribe',
    buttonStyle: {
      backgroundColor: '#8B5CF6',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '20px',
      fontWeight: 500,
      fontSize: '13px',
      border: 'none',
    },
  },
  // Dark Solid
  {
    id: 'dark-solid',
    label: 'Dark Solid',
    buttonText: 'Read More',
    buttonStyle: {
      backgroundColor: '#0F172A',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '4px',
      fontWeight: 500,
      fontSize: '13px',
      border: 'none',
    },
  },
  // Pink Pill
  {
    id: 'pink-pill',
    label: 'Pink Pill',
    buttonText: 'Join Now',
    buttonStyle: {
      background: 'linear-gradient(90deg, #EC4899 0%, #F472B6 100%)',
      color: '#FFFFFF',
      padding: '6px 16px',
      borderRadius: '50px',
      fontWeight: 600,
      fontSize: '13px',
      border: 'none',
    },
  },
  // Amber Ghost
  {
    id: 'amber-ghost',
    label: 'Amber Ghost',
    buttonText: '★ Save',
    buttonStyle: {
      backgroundColor: '#FEF3C7',
      color: '#D97706',
      padding: '6px 12px',
      borderRadius: '6px',
      fontWeight: 600,
      fontSize: '13px',
      border: '1px solid #FDE68A',
    },
  },
  // Teal Solid
  {
    id: 'teal-solid',
    label: 'Teal Solid',
    buttonText: 'Try Free',
    buttonStyle: {
      backgroundColor: '#14B8A6',
      color: '#FFFFFF',
      padding: '6px 14px',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '13px',
      border: 'none',
    },
  },
];

interface DraggableButtonPreviewProps {
  styleConfig: ButtonStyleConfig;
}

const DraggableButtonPreview: React.FC<DraggableButtonPreviewProps> = ({ styleConfig }) => {
  const blockIdRef = React.useRef(`button-preview-${styleConfig.id}-${Math.random().toString(36).substring(2, 9)}`);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: blockIdRef.current,
    data: {
      fromPalette: true,
      type: 'button',
      buttonStyle: styleConfig, // Pass the style configuration
    },
  });

  const transformStyle = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{ ...transformStyle, ...styleConfig.buttonStyle }}
      {...listeners}
      {...attributes}
      className={`text-center whitespace-nowrap cursor-grab active:cursor-grabbing transition-all select-none hover:opacity-80 hover:scale-105 ${isDragging ? 'opacity-50 scale-95' : ''
        }`}
    >
      {styleConfig.buttonText}
    </div>
  );
};

interface DraggableSavedBlockProps {
  savedBlock: SavedBlock;
  onUpdate?: () => void;
  isDeletable?: boolean;
  isRenamable?: boolean;
}

const DraggableSavedBlock: React.FC<DraggableSavedBlockProps> = ({ 
  savedBlock, 
  onUpdate,
  isDeletable = true,
  isRenamable = true
}) => {
  const blockIdRef = React.useRef(`saved-block-${savedBlock.id}`);
  const [showMenu, setShowMenu] = useState(false);
  const [showRenamePopup, setShowRenamePopup] = useState(false);
  const [newName, setNewName] = useState(savedBlock.name);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const renamePopupRef = React.useRef<HTMLDivElement | null>(null);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: blockIdRef.current,
    data: {
      fromPalette: true,
      type: savedBlock.block.type,
      savedBlock: savedBlock.block, // Pass the full block data
    },
  });

  // Handle click outside menu
  React.useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Handle click outside rename popup
  React.useEffect(() => {
    if (!showRenamePopup) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        renamePopupRef.current &&
        !renamePopupRef.current.contains(event.target as Node)
      ) {
        setShowRenamePopup(false);
        setNewName(savedBlock.name);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRenamePopup, savedBlock.name]);

  const handleRename = () => {
    if (newName.trim() && newName.trim() !== savedBlock.name) {
      savedBlocksService.update(savedBlock.id, { name: newName.trim() });
      window.dispatchEvent(new Event('savedBlocksUpdated'));
      setShowRenamePopup(false);
      setShowMenu(false);
      onUpdate?.();
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${savedBlock.name}"?`)) {
      savedBlocksService.delete(savedBlock.id);
      window.dispatchEvent(new Event('savedBlocksUpdated'));
      setShowMenu(false);
      onUpdate?.();
    }
  };

  const style = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-slate-200 rounded-lg bg-white hover:border-ocean-500 hover:shadow-lg transition-all select-none relative ${isDragging ? 'opacity-50 scale-95' : 'hover:scale-[1.02]'
        }`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between mb-2">
          <div
            {...listeners}
            {...attributes}
            className="flex-1 cursor-grab active:cursor-grabbing"
          >
            <div className="text-xs font-normal text-slate-500 truncate" title={savedBlock.name}>
              {savedBlock.name}
            </div>
          </div>
          {/* Menu Button */}
          {(isDeletable || isRenamable) && (
            <div className="relative ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className="p-1 rounded hover:bg-slate-100 transition-colors"
                title="Options"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                  />
                </svg>
              </button>
              {/* Menu Dropdown */}
              {showMenu && (
                <div
                  ref={menuRef}
                  className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[120px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isRenamable && (
                    <button
                      onClick={() => {
                        setShowRenamePopup(true);
                        setShowMenu(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 first:rounded-t-lg transition-colors"
                    >
                      Rename
                    </button>
                  )}
                  {isDeletable && (
                    <button
                      onClick={handleDelete}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 last:rounded-b-lg transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          {...listeners}
          {...attributes}
          className="border border-slate-200 rounded bg-slate-50 overflow-hidden cursor-grab active:cursor-grabbing"
          style={{ height: '80px' }}
        >
          <div style={{ transform: 'scale(0.3)', transformOrigin: 'top left', width: '333%', height: '333%', pointerEvents: 'none' }}>
            <BlockRenderer block={savedBlock.block} />
          </div>
        </div>
      </div>
      {/* Rename Popup */}
      {showRenamePopup && (
        <div
          ref={renamePopupRef}
          className="absolute inset-0 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) {
                handleRename();
              }
              if (e.key === 'Escape') {
                setShowRenamePopup(false);
                setNewName(savedBlock.name);
              }
            }}
            placeholder="Enter block name"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent mb-2"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleRename}
              disabled={!newName.trim() || newName.trim() === savedBlock.name}
              className="flex-1 px-3 py-1.5 bg-ocean-500 text-white text-xs font-medium rounded-md hover:bg-ocean-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => {
                setShowRenamePopup(false);
                setNewName(savedBlock.name);
              }}
              className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface DraggableLayoutProps {
  layout: LayoutTemplate;
}

const DraggableLayout: React.FC<DraggableLayoutProps> = ({ layout }) => {
  const layoutIdRef = React.useRef(`layout-${layout.id}-${Math.random().toString(36).substring(2, 9)}`);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: layoutIdRef.current,
    data: {
      fromPalette: true,
      type: 'layout',
      layout: layout,
    },
  });

  const style = transform
    ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    }
    : undefined;

  // Dynamic preview sizing based on actual rendered content dimensions
  const isHeroLayout = layout.preview === 'Hero';
  const isFooterLayout = layout.preview === 'Footer';
  const BASE_PREVIEW_SCALE = isHeroLayout ? 0.72 : isFooterLayout ? 0.55 : 0.4;
  const MIN_PREVIEW_SCALE = isHeroLayout ? 0.5 : isFooterLayout ? 0.32 : 0.24;
  const [previewHeight, setPreviewHeight] = React.useState<number | undefined>(undefined);
  const [previewScale, setPreviewScale] = React.useState<number>(BASE_PREVIEW_SCALE);
  const previewOuterRef = React.useRef<HTMLDivElement | null>(null);
  const previewInnerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (previewInnerRef.current && previewOuterRef.current) {
      const fullHeight = previewInnerRef.current.scrollHeight || previewInnerRef.current.clientHeight;
      const fullWidth = previewInnerRef.current.scrollWidth || previewInnerRef.current.clientWidth;
      const availableWidth = previewOuterRef.current.clientWidth || 1;
      if (fullHeight > 0) {
        // Fit wide templates inside the thumbnail so content doesn't get cut off.
        const heroTargetHeight = HERO_PALETTE_PREVIEW_HEIGHTS[layout.id];
        const footerTargetHeight = FOOTER_PALETTE_PREVIEW_HEIGHTS[layout.id];
        const targetHeight = heroTargetHeight || footerTargetHeight;
        const fitScaleX = availableWidth / Math.max(fullWidth, 1);
        let nextScale = Math.max(
          MIN_PREVIEW_SCALE,
          Math.min(BASE_PREVIEW_SCALE, fitScaleX)
        );

        if (targetHeight && fullHeight > 0) {
          const fitScaleY = targetHeight / fullHeight;
          nextScale = Math.max(
            MIN_PREVIEW_SCALE,
            Math.min(BASE_PREVIEW_SCALE, fitScaleX, fitScaleY)
          );
        }

        setPreviewScale(nextScale);

        const scaled = fullHeight * nextScale;
        if (targetHeight) {
          setPreviewHeight(targetHeight);
        } else {
          const minHeight =
            layout.preview === 'Menu' ? 34 : layout.preview === 'Header' ? 40 : 72;
          const maxHeight =
            layout.preview === 'Header'
              ? 120
              : layout.preview === 'Menu'
                ? 56
                : 220;
          const clamped = Math.min(Math.max(scaled, minHeight), maxHeight);
          setPreviewHeight(clamped);
        }
      }
    }
  }, [layout]);

  if (layout.isBlank) {
    const columns = layout.previewColumns || [100];
    const total = columns.reduce((sum, value) => sum + value, 0) || 1;

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={`p-3 rounded-xl bg-white cursor-grab active:cursor-grabbing transition-all select-none ${isDragging ? 'opacity-50 scale-95 shadow-sm' : 'hover:scale-[1.01] hover:bg-slate-50'
          }`}
      >
        <div className="h-10 rounded bg-slate-50 flex overflow-hidden border border-slate-300">
          {columns.map((value, index) => (
            <div
              key={index}
              style={{ width: `${(value / total) * 100}%` }}
              className={`h-full flex items-center justify-center text-[11px] font-medium text-slate-600 bg-white ${index < columns.length - 1 ? 'border-r border-slate-300' : ''
                }`}
            >
              {Math.round((value / total) * 100)}%
            </div>
          ))}
        </div>
      </div>
    );
  }

  const renderPreview = () => {
    // Render actual blocks using BlockRenderer for accurate preview
    // Use a container that scales down the content, but height is dynamic based on actual block height
    return (
      <div
        ref={previewOuterRef}
        className="overflow-hidden relative rounded-none bg-slate-50/90"
        style={{
          height: previewHeight,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          ref={previewInnerRef}
          style={{
            transform: `scale(${previewScale})`,
            transformOrigin: 'top left',
            width: `${100 / previewScale}%`,
            pointerEvents: 'none',
          }}
        >
          <style>{FOOTER_BUILDER_CSS}</style>
          {layout.blocks.map((blockData: any, index: number) => {
            const previewBlock: EmailBlockData = {
              id: `preview-${layout.id}-${index}`,
              type: blockData.type,
              content: JSON.parse(JSON.stringify(blockData.content || {})),
              styles: JSON.parse(JSON.stringify(blockData.styles || {})),
            };

            // Normalize columnContent for column blocks - same logic as when adding to canvas
            if (previewBlock.type === 'columns' && previewBlock.content.columnContent) {
              const columnCount = previewBlock.content.columns || previewBlock.content.columnWidths?.length || previewBlock.content.columnContent.length || 2;
              const normalizedColumnContent = Array.from({ length: columnCount }, (_, idx) => {
                const columnItem = previewBlock.content.columnContent[idx];
                if (!columnItem) return [];
                if (Array.isArray(columnItem)) return columnItem;
                if (columnItem && typeof columnItem === 'object' && columnItem.type) return [columnItem];
                return [];
              });
              previewBlock.content.columnContent = normalizedColumnContent;
            }

            return (
              <div
                key={index}
                style={{
                  width: '100%',
                  marginBottom: index < layout.blocks.length - 1 ? 8 : 0,
                }}
              >
                <BlockRenderer block={previewBlock} isMobile={false} isPalettePreview />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`flex flex-col gap-2 cursor-grab active:cursor-grabbing select-none transition-opacity ${isDragging ? 'opacity-60' : ''}`}
    >
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.14em] truncate px-0.5">
        {layout.name}
      </p>
      <div
        className={`rounded-none bg-white border border-slate-200/90 shadow-sm transition-shadow ${isDragging ? 'shadow-md ring-2 ring-ocean-200/50' : 'hover:shadow-md hover:border-slate-300/80'
          }`}
      >
        {renderPreview()}
      </div>
    </div>
  );
};

// Main categories for the left column (Wix-style)
type MainCategory = 'basic' | 'layouts' | 'sections' | 'saved' | 'branding' | 'structure';

// Subcategories for each main category
const CATEGORY_CONFIG: Record<MainCategory, {
  label: string;
  icon: React.ReactNode;
  subcategories: { id: string; label: string }[];
}> = {
  basic: {
    label: 'Basic',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
    subcategories: [
      { id: 'text', label: 'Text & Headings' },
      { id: 'media', label: 'Images & Video' },
      { id: 'buttons', label: 'Buttons' },
      { id: 'dividers', label: 'Dividers & Spacers' },
      { id: 'social', label: 'Social & Links' },
      { id: 'interactive', label: 'Interactive' },
    ],
  },
  layouts: {
    label: 'Grids',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
      </svg>
    ),
    subcategories: [
      { id: 'blank', label: 'Blank Columns' },
    ],
  },
  sections: {
    label: 'Sections',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" />
      </svg>
    ),
    subcategories: [
      { id: 'editorial', label: 'Editorial' },
      { id: 'header', label: 'Header' },
      { id: 'menu', label: 'Menu' },
      { id: 'hero', label: 'Hero' },
      { id: 'products', label: 'Products' },
      { id: 'welcome', label: 'Welcome' },
      { id: 'events', label: 'Events' },
      { id: 'newsletter', label: 'Newsletter' },
      { id: 'testimonials', label: 'Testimonials' },
      { id: 'pricing', label: 'Pricing' },
      { id: 'features', label: 'Features' },
      { id: 'cta', label: 'Call to Action' },
      { id: 'team', label: 'Team' },
      { id: 'faq', label: 'FAQ' },
      { id: 'footers', label: 'Footers' },
    ],
  },
  saved: {
    label: 'Saved',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
    ),
    subcategories: [
      { id: 'recent', label: 'Recent' },
      { id: 'favorites', label: 'Favorites' },
      { id: 'my-blocks', label: 'My Blocks' },
      { id: 'content-files', label: 'Content Files' },
    ],
  },
  branding: {
    label: 'Branding',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.589 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
      </svg>
    ),
    subcategories: [
      { id: 'colors', label: 'Colors' },
      { id: 'fonts', label: 'Fonts' },
      { id: 'signature', label: 'Signature' },
    ],
  },
  structure: {
    label: 'Layout',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
      </svg>
    ),
    subcategories: [
      { id: 'tree', label: 'Structure' },
    ],
  },
};

// Block type to subcategory mapping
const BLOCK_SUBCATEGORY_MAP: Record<string, string> = {
  'header': 'header',
  'heading': 'text',
  'text': 'text',
  'title': 'text',
  'subtitle': 'text',
  'quote': 'text',
  'caption': 'text',
  'preheader': 'text',
  'list': 'text',
  'image': 'media',
  'video': 'media',
  'giphy': 'media',
  'sticker': 'media',
  'image-carousel': 'media',
  'logo': 'media',
  'button': 'buttons',
  'divider': 'dividers',
  'spacer': 'dividers',
  'social': 'social',
  'menu': 'menu',
  'qr-code': 'social',
  'code': 'interactive',
  'countdown': 'interactive',
  'poll': 'interactive',
  'accordion': 'interactive',
  'magic-text': 'interactive',
  'star-rating': 'interactive',
  'product': 'interactive',
  'product-rec': 'interactive',
};

// Section preview to subcategory mapping
const SECTION_SUBCATEGORY_MAP: Record<string, string> = {
  'Header': 'header',
  'Menu': 'menu',
  'Hero': 'hero',
  'Editorial Hero': 'editorial',
  'Editorial Overlay': 'editorial',
  'Hero Card': 'editorial',
  'Editorial Split': 'editorial',
  'Editorial Product': 'editorial',
  'Editorial Quote': 'editorial',
  'Product': 'products',
  'Sale': 'products',
  'Cart': 'products',
  'Welcome': 'welcome',
  'Event': 'events',
  'Conference': 'events',
  'Newsletter': 'newsletter',
  'Testimonial': 'testimonials',
  'Pricing': 'pricing',
  'Features': 'features',
  'CTA': 'cta',
  'Promo': 'cta',
  'Team': 'team',
  'Blog': 'newsletter',
  'FAQ': 'faq',
  'Food': 'products',
  'Property': 'products',
  'Fitness': 'products',
  'Footer': 'footers',
};

interface BlockPaletteProps {
  selectedBlock?: EmailBlockData | null;
  onBlockUpdate?: (updates: Partial<EmailBlockData>) => void;
  onUpdateBlocks?: (blocks: EmailBlockData[]) => void;
  onSelectBlock?: (blockId: string) => void;
  onDeselectBlock?: () => void;
  allBlocks?: EmailBlockData[];
  onClose?: () => void;
  initialCategory?: MainCategory;
  /** When true, hides the 52px icon rail (used when palette is inside a pre-categorised floating panel) */
  hideIconRail?: boolean;
}

/**
 * Left rail under Layout. Round control with Canva mark (Simple Icons, MIT — see /public/images/canva-mark.svg).
 */
const BlockPaletteCanvaSidebarButton: React.FC = () => {
  const ctx = useContext(CanvaImportGuideContext);
  if (!ctx) return null;
  return (
    <div className="w-full flex flex-col items-center border-t border-slate-200 pt-2 pb-2.5 px-1">
      <button
        type="button"
        onClick={ctx.openGuide}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-slate-200/90 transition-all hover:ring-teal-400/70 hover:shadow-lg hover:scale-[1.04] active:scale-[0.97] overflow-hidden"
        title="Use a design from Canva (export PNG, then upload)"
        aria-label="Open Canva import guide"
      >
        <img src="/images/canva-mark.svg" alt="" className="h-full w-full object-cover" draggable={false} />
      </button>
      <span className="text-[9px] font-medium text-slate-500 mt-1 text-center leading-tight">Canva</span>
    </div>
  );
};

export const BlockPalette: React.FC<BlockPaletteProps> = ({
  selectedBlock,
  onBlockUpdate,
  onUpdateBlocks,
  onSelectBlock,
  onDeselectBlock,
  allBlocks = [],
  onClose,
  initialCategory,
  hideIconRail = false,
}) => {
  const [activeCategory, setActiveCategory] = useState<MainCategory>(initialCategory || 'basic');
  const [activeSubcategory, setActiveSubcategory] = useState<string>(
    initialCategory === 'sections' ? 'hero'
    : initialCategory === 'saved' ? 'recent'
    : 'text'
  );
  // For the flat hideIconRail panel — which subcategory row is expanded
  const [expandedSubcat, setExpandedSubcat] = useState<string | null>(
    initialCategory === 'sections' ? 'hero'
    : initialCategory === 'saved' ? 'recent'
    : 'text'
  );
  const [savedBlocks, setSavedBlocks] = useState<SavedBlock[]>([]);
  const [contentFiles, setContentFiles] = useState<StorageFile[]>([]);
  const [contentFilesLoading, setContentFilesLoading] = useState(false);
  const [contentFilesError, setContentFilesError] = useState<string | null>(null);
  const CONTENT_FILES_BATCH = 6;
  const [visibleContentCount, setVisibleContentCount] = useState(CONTENT_FILES_BATCH);
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null);
  const { currentWorkspace } = useWorkspace();

  const formattedAddress = useMemo(() => {
    const address = currentWorkspace?.settings?.address;
    if (!address) return '';
    const line1 = [address.addressLine1, address.addressLine2].filter((l: string | undefined) => l?.trim()).join(', ');
    const line2 = [
      address.city ? `${address.city}${address.region ? `, ${address.region}` : ''} ${address.postalCode || ''}` : '',
      address.country
    ].filter((l: string | undefined) => l?.trim()).join(', ');
    const result = [line1, line2].filter((l: string) => l);
    return result.length > 0 ? result.join(', ') : undefined;
  }, [currentWorkspace?.settings?.address]);

  const dynamicPrebuiltLayouts = useMemo(() => {
    if (!formattedAddress) return PREBUILT_LAYOUTS;
    return PREBUILT_LAYOUTS.map(layout => {
      // Deep clone to avoid mutating the constant
      const newLayout = JSON.parse(JSON.stringify(layout));
      newLayout.blocks.forEach((block: any) => {
        if (block.type === 'footer' && block.content) {
          if (block.content.companyInfo?.includes('123 Main Street')) {
            block.content.companyInfo = `© ${currentWorkspace?.name || 'Your Company'}, ${formattedAddress}`;
          }
        }
      });
      return newLayout;
    });
  }, [formattedAddress, currentWorkspace?.name]);

  const { currentUser } = useAuth();
  const workspaceBranding = currentWorkspace?.settings?.branding as { primaryColor?: string; secondaryColor?: string; palette?: { accent?: string }; headingFont?: string; bodyFont?: string; emailSignature?: string } | undefined;
  const brandingSettingsPath = currentWorkspace?.id
    ? `/workspace/${currentWorkspace.id}/settings/branding`
    : null;
  const [isContentLibraryOpen, setIsContentLibraryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const isAutoScrolling = React.useRef(false);
  // Ref map for hideIconRail panel scroll-to-section
  const panelSectionRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Sync active category with scroll position
  useEffect(() => {
    if (activeCategory !== 'sections' || !scrollContainerRef.current) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '-10% 0px -80% 0px', // Trigger when close to top
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      if (isAutoScrolling.current) return;

      // Find the topmost intersecting entry
      const visibleEntries = entries.filter(e => e.isIntersecting);
      if (visibleEntries.length > 0) {
        // Sort by how close they are to the top of the container
        const subcatId = visibleEntries[0].target.getAttribute('data-subcat-id');
        if (subcatId && subcatId !== activeSubcategory) {
          setActiveSubcategory(subcatId);
        }
      }
    }, options);

    const elements = Object.values(sectionRefs.current).filter(Boolean);
    elements.forEach((el) => observer.observe(el!));

    return () => {
      elements.forEach((el) => observer.unobserve(el!));
    };
  }, [activeCategory]);

  const openBrandingSettings = useCallback(() => {
    if (!brandingSettingsPath) return;
    window.open(brandingSettingsPath, '_blank');
  }, [brandingSettingsPath]);

  const loadContentFiles = useCallback(async () => {
    if (!currentUser) return;
    setContentFilesLoading(true);
    setContentFilesError(null);
    try {
      const files = await storageService.getFiles(
        currentUser.uid,
        currentWorkspace?.id,
        undefined,
        18
      );
      setContentFiles(files);
      setVisibleContentCount(Math.min(CONTENT_FILES_BATCH, files.length));
    } catch (error) {
      console.error('Error loading content files:', error);
      setContentFilesError('Unable to load files. Please try again.');
    } finally {
      setContentFilesLoading(false);
    }
  }, [currentUser, currentWorkspace?.id]);

  useEffect(() => {
    if (activeCategory === 'saved' && activeSubcategory === 'content-files' && currentUser) {
      loadContentFiles();
    }
  }, [activeCategory, activeSubcategory, currentUser, loadContentFiles]);

  // Load saved blocks
  useEffect(() => {
    const loadSavedBlocks = () => {
      setSavedBlocks(savedBlocksService.getAll());
    };
    loadSavedBlocks();

    // Listen for custom event when blocks are saved
    const handleSavedBlocksUpdate = () => {
      loadSavedBlocks();
    };
    window.addEventListener('savedBlocksUpdated', handleSavedBlocksUpdate);

    // Listen for storage changes (when blocks are saved from other tabs)
    const handleStorageChange = () => {
      loadSavedBlocks();
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('savedBlocksUpdated', handleSavedBlocksUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Properties are now shown in the dedicated right-side panel,
  // so we no longer auto-switch tabs when a block is selected

  // Helper to get blocks for a subcategory
  const getBlocksForSubcategory = (subcategory: string) => {
    return BLOCK_TYPES.filter(block => BLOCK_SUBCATEGORY_MAP[block.type] === subcategory);
  };

  // Helper to get sections for a subcategory  
  const getSectionsForSubcategory = (subcategory: string) => {
    if (subcategory === 'footers') {
      return dynamicPrebuiltLayouts.filter((l: LayoutTemplate) => l.id.startsWith('footer-'));
    }
    return dynamicPrebuiltLayouts.filter((l: LayoutTemplate) => {
      const sectionSubcat = SECTION_SUBCATEGORY_MAP[l.preview];
      return sectionSubcat === subcategory && l.id.startsWith('layout-');
    });
  };

  // Filter blocks by search query
  const getFilteredBlocks = useCallback(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    return BLOCK_TYPES.filter(
      block =>
        block.label.toLowerCase().includes(query) ||
        block.description.toLowerCase().includes(query) ||
        block.type.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Filter layouts by search query
  const getFilteredLayouts = useCallback(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    const blankFiltered = BLANK_LAYOUTS.filter(
      (layout: LayoutTemplate) => layout.name.toLowerCase().includes(query) || layout.description.toLowerCase().includes(query)
    );
    const prebuiltFiltered = dynamicPrebuiltLayouts.filter(
      (layout: LayoutTemplate) => layout.name.toLowerCase().includes(query) || layout.description.toLowerCase().includes(query)
    );
    return { blank: blankFiltered, prebuilt: prebuiltFiltered };
  }, [searchQuery]);

  // Handle category change and set first subcategory
  const handleCategoryChange = (category: MainCategory) => {
    setActiveCategory(category);
    setSearchQuery(''); // Clear search when switching categories
    const firstSubcat = CATEGORY_CONFIG[category].subcategories[0];
    if (firstSubcat) {
      setActiveSubcategory(firstSubcat.id);
    }
  };

  // Render content based on category and subcategory
  const renderContent = (subcatOverride?: string, catOverride?: MainCategory) => {
    const effectiveSubcat = subcatOverride ?? activeSubcategory;
    const effectiveCat = catOverride ?? activeCategory;
    // Show search results if there's a search query
    const filteredBlocks = getFilteredBlocks();
    const filteredLayouts = getFilteredLayouts();

    if (searchQuery.trim()) {
      const hasBlocks = filteredBlocks && filteredBlocks.length > 0;
      const hasLayouts = filteredLayouts && (filteredLayouts.blank.length > 0 || filteredLayouts.prebuilt.length > 0);

      if (!hasBlocks && !hasLayouts) {
        return (
          <div className="p-6 text-center">
            <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="text-sm text-slate-500">No blocks found for "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-teal-600 hover:text-teal-700"
            >
              Clear search
            </button>
          </div>
        );
      }

      return (
        <div className="p-4">
          <p className="text-xs text-slate-500 mb-3">
            {(filteredBlocks?.length || 0) + (filteredLayouts?.blank.length || 0) + (filteredLayouts?.prebuilt.length || 0)} results for "{searchQuery}"
          </p>

          {hasBlocks && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Blocks</h4>
              <div className="flex flex-col gap-1">
                {filteredBlocks?.map((block: BlockItem) => (
                  <DraggableBlock key={block.type} block={block} />
                ))}
              </div>
            </div>
          )}

          {filteredLayouts && filteredLayouts.blank.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Layouts</h4>
              <div className="grid grid-cols-2 gap-2">
                {filteredLayouts.blank.map((layout: LayoutTemplate) => (
                  <DraggableLayout key={layout.id} layout={layout} />
                ))}
              </div>
            </div>
          )}

          {filteredLayouts && filteredLayouts.prebuilt.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Pre-built Sections</h4>
              <div className="flex flex-col gap-5">
                {filteredLayouts.prebuilt.map((layout: LayoutTemplate) => (
                  <DraggableLayout key={layout.id} layout={layout} />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Basic blocks - special handling for Text & Headings subcategory
    if (effectiveCat === 'basic') {
      // Text & Headings gets a special vertical layout with styled previews
      if (effectiveSubcat === 'text') {
        return (
          <div className="p-4">
            <div className="space-y-1">
              {/* Heading - Large display */}
              <DraggableTextPreview
                type="heading"
                previewText="Heading 1"
                style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}
              />
              {/* Title - Extra large */}
              <DraggableTextPreview
                type="title"
                previewText="Title"
                style={{ fontSize: '32px', fontWeight: 800, color: '#0F172A' }}
              />
              {/* Subtitle */}
              <DraggableTextPreview
                type="subtitle"
                previewText="Subtitle"
                style={{ fontSize: '18px', fontWeight: 500, color: '#64748B' }}
              />
              {/* Quote */}
              <DraggableTextPreview
                type="quote"
                previewText={"\"Quote text\""}
                style={{ fontSize: '16px', fontWeight: 400, fontStyle: 'italic', color: '#374151' }}
              />
              {/* Regular text */}
              <DraggableTextPreview
                type="text"
                previewText="Add paragraph text. Click to edit."
                style={{ fontSize: '14px', fontWeight: 400, color: '#374151', lineHeight: '1.5' }}
              />
              {/* Caption */}
              <DraggableTextPreview
                type="caption"
                previewText="Caption text for images"
                style={{ fontSize: '12px', fontWeight: 400, color: '#9CA3AF' }}
              />
              {/* Preheader */}
              <DraggableTextPreview
                type="preheader"
                previewText="Preheader preview text"
                style={{ fontSize: '12px', fontWeight: 400, color: '#6B7280' }}
              />
              {/* List */}
              <DraggableTextPreview
                type="list"
                previewText="• List item"
                style={{ fontSize: '14px', fontWeight: 400, color: '#374151' }}
              />
            </div>
          </div>
        );
      }

      // Buttons - special styled button previews grid
      if (effectiveSubcat === 'buttons') {
        // Create a brand button style if workspace has a primary color
        const brandButtonStyles: ButtonStyleConfig[] = [];
        if (workspaceBranding?.primaryColor) {
          brandButtonStyles.push({
            id: 'brand-primary',
            label: 'Brand Primary',
            buttonText: 'Click Here',
            buttonStyle: {
              backgroundColor: workspaceBranding.primaryColor,
              color: '#FFFFFF',
              padding: '6px 14px',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '13px',
              border: 'none',
            },
          });
        }
        if (workspaceBranding?.secondaryColor) {
          brandButtonStyles.push({
            id: 'brand-secondary',
            label: 'Brand Secondary',
            buttonText: 'Learn More',
            buttonStyle: {
              backgroundColor: workspaceBranding.secondaryColor,
              color: '#FFFFFF',
              padding: '6px 14px',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '13px',
              border: 'none',
            },
          });
        }

        const allButtonStyles = [...brandButtonStyles, ...BUTTON_STYLES];

        return (
          <div className="p-4">
            {brandButtonStyles.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Your Brand</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {brandButtonStyles.map((styleConfig: ButtonStyleConfig) => (
                    <DraggableButtonPreview key={styleConfig.id} styleConfig={styleConfig} />
                  ))}
                </div>
                <hr className="border-slate-200" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {BUTTON_STYLES.map((styleConfig: ButtonStyleConfig) => (
                <DraggableButtonPreview key={styleConfig.id} styleConfig={styleConfig} />
              ))}
            </div>
          </div>
        );
      }

      // Other basic subcategories use the regular single-column list
      const blocks = getBlocksForSubcategory(effectiveSubcat);
      return (
        <div className="px-3 py-3">
          <div className="flex flex-col gap-1">
            {blocks.map((block: BlockItem) => (
              <DraggableBlock key={block.type} block={block} />
            ))}
          </div>
          {blocks.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <p className="text-sm">No blocks in this category</p>
            </div>
          )}
        </div>
      );
    }

    // Layouts
    if (effectiveCat === 'layouts') {
      return (
        <div className="p-4 space-y-3">
          {BLANK_LAYOUTS.map((layout: LayoutTemplate) => (
            <DraggableLayout key={layout.id} layout={layout} />
          ))}
        </div>
      );
    }

    // Sections
    if (effectiveCat === 'sections') {
      const subcategories = CATEGORY_CONFIG.sections.subcategories;
      return (
        <div className="px-4 py-5 space-y-10">
          {subcategories.map((subcat, index) => {
            const sections = getSectionsForSubcategory(subcat.id);
            if (sections.length === 0) return null;

            return (
              <div
                key={subcat.id}
                data-subcat-id={subcat.id}
                ref={(el) => { sectionRefs.current[subcat.id] = el; }}
                className="scroll-mt-6"
              >
                <div className="flex items-center gap-2.5 mb-4 sticky top-0 z-10 py-2 -mx-1 px-1 bg-[#F7F8FA]/95 backdrop-blur-sm">
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.1em]">{subcat.label}</h4>
                  <div className="flex-1 h-px bg-black/[0.07]" />
                </div>

                <div className="flex flex-col gap-6">
                  {sections.map((layout: LayoutTemplate) => (
                    <DraggableLayout key={layout.id} layout={layout} />
                  ))}
                </div>

                {index < subcategories.length - 1 && (
                  <div className="pt-8">
                    <hr className="border-slate-200/70" />
                  </div>
                )}
              </div>
            );
          })}
          {/* Bottom spacer to allow the last section to scroll to the top */}
          <div className="h-64" />
        </div>
      );
    }

    // Saved blocks
    if (effectiveCat === 'saved') {
      // Recent blocks section
      if (effectiveSubcat === 'recent') {
        const recentTypes = recentBlocksService.getRecentTypes(10);
        const recentBlocks = BLOCK_TYPES.filter(block => recentTypes.includes(block.type));

        return (
          <div className="p-4">
            {recentBlocks.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium">No recent blocks</p>
                <p className="text-xs mt-1">Blocks you use will appear here</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {recentBlocks.map((block) => (
                  <DraggableBlock key={block.type} block={block} />
                ))}
              </div>
            )}
          </div>
        );
      }

      // Favorites section
      if (effectiveSubcat === 'favorites') {
        const favoriteTypes = recentBlocksService.getFavorites();
        const favoriteBlocks = BLOCK_TYPES.filter(block => favoriteTypes.includes(block.type));

        return (
          <div className="p-4">
            {favoriteBlocks.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <p className="text-sm font-medium">No favorites yet</p>
                <p className="text-xs mt-1">Click the heart icon on blocks to add favorites</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {favoriteBlocks.map((block) => (
                  <DraggableBlock key={block.type} block={block} />
                ))}
              </div>
            )}
          </div>
        );
      }

      if (effectiveSubcat === 'my-blocks') {
        return (
          <div className="p-4">
            {savedBlocks.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-sm font-medium">No saved blocks yet</p>
                <p className="text-xs mt-1">Save blocks from the canvas to reuse them</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {savedBlocks.map((savedBlock: SavedBlock) => (
                  <DraggableSavedBlock
                    key={savedBlock.id}
                    savedBlock={savedBlock}
                    onUpdate={() => setSavedBlocks(savedBlocksService.getAll())}
                  />
                ))}
              </div>
            )}
          </div>
        );
      }

      if (effectiveSubcat === 'content-files') {
        return (
          <div className="p-4">
            <button
              type="button"
              onClick={() => setIsContentLibraryOpen(true)}
              className="w-full mb-4 px-3 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors"
            >
              Open Content Library
            </button>
            {contentFilesLoading ? (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">Loading files…</p>
              </div>
            ) : contentFiles.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">No files uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {contentFiles.slice(0, visibleContentCount).map((file, index) => {
                  const isImage = file.type === 'image';
                  const previewSrc = file.thumbnailUrl || (isImage ? file.url : undefined);
                  const draggableId = file.id
                    ? `content-file-${file.id}`
                    : `content-file-${index}-${file.originalName}`;

                  return (
                    <DraggableContentFile key={draggableId} file={file} draggableId={draggableId}>
                      <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center overflow-hidden">
                        {previewSrc ? (
                          <img src={previewSrc} alt={file.originalName} className="object-cover w-full h-full" />
                        ) : (
                          <DocumentIcon className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <p className="text-xs font-medium text-slate-700 truncate flex-1" title={file.originalName}>
                        {file.originalName}
                      </p>
                    </DraggableContentFile>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
    }

    // Branding
    if (effectiveCat === 'branding') {
      if (effectiveSubcat === 'colors') {
        const hasColors = workspaceBranding?.primaryColor || workspaceBranding?.secondaryColor || workspaceBranding?.palette?.accent;
        return (
          <div className="p-4">
            {hasColors ? (
              <div className="space-y-3">
                {workspaceBranding?.primaryColor && (
                  <BrandingColorSwatch label="Primary" color={workspaceBranding.primaryColor} />
                )}
                {workspaceBranding?.secondaryColor && (
                  <BrandingColorSwatch label="Secondary" color={workspaceBranding.secondaryColor} />
                )}
                {workspaceBranding?.palette?.accent && (
                  <BrandingColorSwatch label="Accent" color={workspaceBranding.palette.accent} />
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">No brand colors set</p>
                {brandingSettingsPath && (
                  <button
                    onClick={openBrandingSettings}
                    className="mt-2 text-xs text-ocean-500 hover:underline"
                  >
                    Set up branding
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }

      if (effectiveSubcat === 'fonts') {
        const hasFonts = workspaceBranding?.headingFont || workspaceBranding?.bodyFont;
        return (
          <div className="p-4">
            {hasFonts ? (
              <div className="space-y-3">
                {workspaceBranding?.headingFont && (
                  <BrandingFontPreview label="Heading" font={workspaceBranding.headingFont} />
                )}
                {workspaceBranding?.bodyFont && (
                  <BrandingFontPreview label="Body" font={workspaceBranding.bodyFont} />
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">No brand fonts set</p>
                {brandingSettingsPath && (
                  <button
                    onClick={openBrandingSettings}
                    className="mt-2 text-xs text-ocean-500 hover:underline"
                  >
                    Set up branding
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }

      if (effectiveSubcat === 'signature') {
        const hasSignature = workspaceBranding?.emailSignature;
        return (
          <div className="p-4">
            {hasSignature ? (
              <div className="space-y-4">
                <p className="text-xs text-slate-500 mb-2">Drag your signature to any section of the email.</p>
                <DraggableSavedBlock
                  savedBlock={{
                    id: 'workspace-signature',
                    name: 'My Signature',
                    block: {
                      id: 'workspace-signature-block',
                      type: 'text',
                      content: { text: workspaceBranding.emailSignature },
                      styles: {
                        padding: '20px',
                        fontSize: '14px',
                        color: '#6B7280',
                        textAlign: 'left',
                        lineHeight: '1.6'
                      },
                      isSignature: true // Flag to prevent duplication in final HTML
                    },
                    createdAt: Date.now(),
                    updatedAt: Date.now()
                  }}
                  onUpdate={() => {}}
                  isDeletable={false}
                  isRenamable={false}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">No email signature set</p>
                {brandingSettingsPath && (
                  <button
                    onClick={openBrandingSettings}
                    className="mt-2 text-xs text-ocean-500 hover:underline"
                  >
                    Set up signature
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }
    }

    if (effectiveCat === 'structure') {
      return (
        <LayoutTreePanel
          blocks={allBlocks}
          onUpdateBlocks={onUpdateBlocks || (() => {})}
          onSelectBlock={onSelectBlock || (() => {})}
          selectedBlockId={selectedBlock?.id || null}
          onUpdateBlock={(id, updates) => {
            if (onBlockUpdate && selectedBlock?.id === id) {
              onBlockUpdate(updates);
            } else if (onUpdateBlocks) {
              const newBlocks = allBlocks.map(b => b.id === id ? { ...b, ...updates } : b);
              onUpdateBlocks(newBlocks);
            }
          }}
        />
      );
    }

    return null;
  };

  const renderContentForSubcat = (subcatId: string): React.ReactNode => {
    return renderContent(subcatId);
  };

  // ─── hideIconRail mode: clean flat single-column layout ─────────────────────
  if (hideIconRail) {
    const isSectionsMode = initialCategory === 'sections';

    // ── SHARED sidebar definition ──────────────────────────────────────
    type RailItem = { id: string; label: string; mainCat: MainCategory; icon: React.ReactNode };

    const elementRailItems: RailItem[] = [
      {
        id: 'text', label: 'Text', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10M4 18h10M20 6v12" /></svg>,
      },
      {
        id: 'media', label: 'Image', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" /></svg>,
      },
      {
        id: 'buttons', label: 'Button', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><rect x="3" y="8" width="18" height="8" rx="4" strokeLinecap="round" strokeLinejoin="round" /><path strokeLinecap="round" strokeLinejoin="round" d="M9.5 12h5" /></svg>,
      },
      {
        id: 'dividers', label: 'Divider', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18M8 8l-4 4 4 4M16 8l4 4-4 4" /></svg>,
      },
      {
        id: 'social', label: 'Social', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" /></svg>,
      },
      {
        id: 'interactive', label: 'More', mainCat: 'basic',
        icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>,
      },
    ];

    const sectionRailItems: RailItem[] = [
      { id: 'editorial', label: 'Editorial', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h8M4 18h8" /></svg> },
      { id: 'header', label: 'Header', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h18M3 9h18M3 13.5h18" /></svg> },
      { id: 'menu', label: 'Menu', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" /></svg> },
      { id: 'hero', label: 'Hero', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.5h19.5M2.25 12h19.5M2.25 16.5H12" /></svg> },
      { id: 'products', label: 'Products', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z" /></svg> },
      { id: 'welcome', label: 'Welcome', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg> },
      { id: 'events', label: 'Events', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg> },
      { id: 'newsletter', label: 'Newsletter', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg> },
      { id: 'testimonials', label: 'Testimonials', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg> },
      { id: 'pricing', label: 'Pricing', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" /></svg> },
      { id: 'features', label: 'Features', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6z" /></svg> },
      { id: 'cta', label: 'CTA', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" /></svg> },
      { id: 'team', label: 'Team', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></svg> },
      { id: 'faq', label: 'FAQ', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg> },
      { id: 'footers', label: 'Footer', mainCat: 'sections', icon: <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21l3.75-3.75" /></svg> },
    ];

    const railItems = isSectionsMode ? sectionRailItems : elementRailItems;
    const defaultRailId = isSectionsMode ? 'editorial' : 'text';

    // Sync active category
    if (isSectionsMode && activeCategory !== 'sections') {
      // Will be set on click, we just ensure initial state
    }

    const activeRailId = expandedSubcat ?? defaultRailId;
    const activeRailItem = railItems.find(r => r.id === activeRailId) ?? railItems[0];

    const scrollToSection = (id: string) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      // For sections mode, use the existing sectionRefs set by renderContent
      const el = isSectionsMode ? sectionRefs.current[id] : panelSectionRefs.current[id];
      if (!el) return;
      // Walk offsetParent chain to get offset relative to the scroll container
      let offsetTop = 0;
      let node: HTMLElement | null = el;
      while (node && node !== container) {
        offsetTop += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      container.scrollTo({ top: offsetTop - 8, behavior: 'smooth' });
    };

    // Sync sidebar highlight as user scrolls
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            // sections mode uses data-subcat-id; elements mode uses data-panel-section-id
            const id = visible[0].target.getAttribute('data-subcat-id')
                    ?? visible[0].target.getAttribute('data-panel-section-id');
            if (id) setExpandedSubcat(id);
          }
        },
        {
          root: container,
          // Trigger when section heading crosses top 30% of the scroll container
          rootMargin: '0px 0px -70% 0px',
          threshold: 0,
        }
      );

      // Observe all section heading elements
      const refs = isSectionsMode ? sectionRefs.current : panelSectionRefs.current;
      Object.entries(refs).forEach(([, el]) => {
        if (el) observer.observe(el);
      });

      return () => observer.disconnect();
    // Re-run when railItems change (elements vs sections mode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSectionsMode]);

    return (
      <div className="flex h-full bg-transparent">
        {/* ── Clean icon rail ────────────────────────────────────────── */}
        <div className={`${isSectionsMode ? 'w-[58px]' : 'w-[56px]'} flex-shrink-0 flex flex-col items-center pt-2 pb-2 gap-0.5 overflow-y-auto no-scrollbar hide-scrollbar border-r border-black/[0.06]`}>
          {railItems.map((item) => {
            const isActive = activeRailId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                title={item.label}
                onClick={() => {
                  setExpandedSubcat(item.id);
                  setActiveSubcategory(item.id);
                  if (item.mainCat !== activeCategory) handleCategoryChange(item.mainCat);
                  setTimeout(() => scrollToSection(item.id), 50);
                }}
                className={`relative flex-shrink-0 flex flex-col items-center justify-center w-full ${isSectionsMode ? 'h-[52px] py-1' : 'h-11'} rounded-lg transition-all duration-150 group ${
                  isActive
                    ? 'text-slate-900'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-black/[0.05]'
                }`}
              >
                {/* Active left-edge indicator */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-r-full bg-slate-900" />
                )}
                {/* Active bg pill */}
                {isActive && (
                  <span className="absolute inset-0 rounded-lg bg-slate-900/[0.07]" />
                )}
                <span className={`relative ${isSectionsMode ? '' : ''}`}>
                  {item.icon}
                </span>
                <span className={`relative ${isSectionsMode ? 'text-[9px]' : 'text-[9px]'} font-medium mt-0.5 leading-none tracking-tight ${isActive ? 'text-slate-800' : 'text-slate-600 group-hover:text-slate-900'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Main content area ──────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Search */}
          <div className="px-3 pt-3 pb-2 flex-shrink-0">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search elements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-[12px] rounded-lg bg-black/[0.04] border-0 focus:bg-black/[0.07] focus:ring-0 outline-none transition-all placeholder:text-slate-400 text-slate-700"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content — rendered once; sidebar click scrolls to anchors */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto no-scrollbar hide-scrollbar scroll-smooth"
          >
            {isSectionsMode ? (
              /* Sections mode: renderContent renders all subcats with sectionRefs already set */
              <div>{renderContent(undefined, 'sections')}</div>
            ) : (
              /* Elements mode: render each category with panelSectionRefs anchors */
              <>
                {railItems.map((item) => (
                  <div
                    key={item.id}
                    ref={(el) => { panelSectionRefs.current[item.id] = el; }}
                    data-panel-section-id={item.id}
                    className="px-2"
                  >
                    <div className="px-2 pt-4 pb-2">
                      <h3 className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.1em]">{item.label}</h3>
                    </div>
                    <div className="pb-4">
                      {renderContent(item.id, item.mainCat)}
                    </div>
                  </div>
                ))}
                <div className="h-6" />
              </>
            )}
          </div>
        </div>

        {/* Modals */}
        {isContentLibraryOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setIsContentLibraryOpen(false)}>
            <div className="bg-white w-full max-w-6xl h-[88vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <div><h3 className="text-base font-semibold text-slate-900">Content Manager</h3><p className="text-xs text-slate-500">Browse and manage your saved assets</p></div>
                <button type="button" onClick={() => setIsContentLibraryOpen(false)} className="p-2 rounded-full hover:bg-slate-100 transition-colors"><svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
              <div className="flex-1 overflow-hidden bg-slate-50"><div className="h-full overflow-y-auto"><ContentManager /></div></div>
            </div>
          </div>
        )}
        {previewFile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewFile(null)}>
            <div className="bg-white max-w-4xl w-full mx-6 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <div className="min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{previewFile.originalName}</p><p className="text-xs text-slate-500">{previewFile.type.charAt(0).toUpperCase() + previewFile.type.slice(1)}</p></div>
                <button type="button" onClick={() => setPreviewFile(null)} className="p-2 rounded-full hover:bg-slate-100 transition-colors"><svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
              <div className="p-5 bg-slate-50">
                {previewFile.type === 'media'
                  ? <video src={previewFile.url} controls className="w-full max-h-[70vh] rounded-xl bg-black" autoPlay playsInline />
                  : <img src={previewFile.url} alt={previewFile.originalName} className="w-full max-h-[70vh] object-contain rounded-xl bg-white" />
                }
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Normal (docked sidebar) mode ─────────────────────────────────────────
  return (
    <div className="flex h-full">
      {/* Column 1: Main Categories — hidden when palette is inside a floating panel */}
      {!hideIconRail && (
        <div className="w-[52px] flex-shrink-0 border-r border-black/[0.07] bg-[#F7F8FA] overflow-y-auto">
          {(Object.keys(CATEGORY_CONFIG) as MainCategory[]).map((category) => {
            const config = CATEGORY_CONFIG[category];
            const isActive = activeCategory === category;
            return (
              <React.Fragment key={category}>
                <button
                  onClick={() => handleCategoryChange(category)}
                  className={`w-full flex flex-col items-center justify-center py-3.5 px-1 transition-all ${isActive
                    ? 'text-slate-900 bg-white border-r-2 border-slate-900'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-black/[0.04]'
                    }`}
                  title={config.label}
                >
                  <div className="mb-1">{config.icon}</div>
                  <span className="text-[9px] font-semibold leading-tight text-center uppercase tracking-wide">{config.label}</span>
                </button>
                {category === 'structure' ? <BlockPaletteCanvaSidebarButton /> : null}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Column 2: Subcategories */}
      {activeCategory !== 'structure' && (
        <div className={`${hideIconRail ? 'w-44' : 'w-36'} flex-shrink-0 border-r border-black/[0.07] bg-white overflow-y-auto`}>
          <div className="py-2">
            {!hideIconRail && (
              <h3 className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-[0.1em] mb-1 mt-1">
                {CATEGORY_CONFIG[activeCategory].label}
              </h3>
            )}
            {hideIconRail && (
              <h3 className="px-3 pt-1 pb-2 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">
                {CATEGORY_CONFIG[activeCategory].label}
              </h3>
            )}
            {CATEGORY_CONFIG[activeCategory].subcategories.map((subcat) => {
              const isActive = activeSubcategory === subcat.id;
              return (
                <button
                  key={subcat.id}
                  onClick={() => {
                    setActiveSubcategory(subcat.id);
                    if (activeCategory === 'sections') {
                      const element = sectionRefs.current[subcat.id];
                      if (element && scrollContainerRef.current) {
                        isAutoScrolling.current = true;
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        setTimeout(() => { isAutoScrolling.current = false; }, 1000);
                      }
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-[12px] transition-colors rounded-none ${isActive
                    ? 'bg-black/[0.06] text-slate-900 font-semibold'
                    : 'text-slate-500 hover:bg-black/[0.03] hover:text-slate-800'
                    }`}
                >
                  {subcat.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Column 3: Content/Previews */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto flex flex-col scroll-smooth bg-[#F7F8FA]"
      >
        {/* Category pill tab strip — only shown when icon rail is hidden */}
        {hideIconRail && (
          <div className="flex items-center gap-1 px-3 pt-3 pb-2 flex-shrink-0 bg-[#F7F8FA]">
            {(['basic', 'sections', 'saved'] as const).map((cat) => {
              const labels: Record<string, string> = { basic: 'Elements', sections: 'Sections', saved: 'Saved' };
              const icons: Record<string, React.ReactNode> = {
                basic: (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h8M4 18h8" />
                  </svg>
                ),
                sections: (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4H5a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V5a1 1 0 00-1-1zm10 0h-4a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V5a1 1 0 00-1-1zM9 14H5a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1v-4a1 1 0 00-1-1zm10 0h-4a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1v-4a1 1 0 00-1-1z" />
                  </svg>
                ),
                saved: (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                ),
              };
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => handleCategoryChange(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'bg-white border border-black/[0.09] text-slate-500 hover:text-slate-800 hover:border-black/[0.16] shadow-sm'
                  }`}
                >
                  {icons[cat]}
                  {labels[cat]}
                </button>
              );
            })}
          </div>
        )}

        {/* Search bar */}
        <div className={`flex items-center gap-2 px-2.5 py-2 flex-shrink-0 border-b border-black/[0.06] bg-white ${hideIconRail ? '' : ''}`}>
          <div className="relative flex-1">
            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search blocks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-7 pr-7 py-1.5 text-xs rounded-md border border-black/[0.1] bg-transparent focus:bg-white focus:border-black/20 focus:ring-0 outline-none transition-all placeholder:text-slate-400 text-slate-700"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {onClose && !hideIconRail && (
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-black/[0.05] rounded transition-colors flex-shrink-0" title="Close panel">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {renderContent()}
      </div>

      {/* Modals */}
      {isContentLibraryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setIsContentLibraryOpen(false)}
        >
          <div
            className="bg-white w-full max-w-6xl h-[88vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Content Manager</h3>
                <p className="text-xs text-slate-500">Browse and manage your saved assets</p>
              </div>
              <button
                type="button"
                onClick={() => setIsContentLibraryOpen(false)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-50">
              <div className="h-full overflow-y-auto">
                <ContentManager />
              </div>
            </div>
          </div>
        </div>
      )}

      {previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="bg-white max-w-4xl w-full mx-6 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{previewFile.originalName}</p>
                <p className="text-xs text-slate-500">{previewFile.type.charAt(0).toUpperCase() + previewFile.type.slice(1)}</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewFile(null)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              >
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 bg-slate-50">
              {previewFile.type === 'media' ? (
                <video
                  src={previewFile.url}
                  controls
                  className="w-full max-h-[70vh] rounded-xl bg-black"
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={previewFile.url}
                  alt={previewFile.originalName}
                  className="w-full max-h-[70vh] object-contain rounded-xl bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

